import { create } from "zustand";
import { authHeaderScriptCache, buildStoredAuthHeaders, executeHeaderScript as executeSharedHeaderScript } from "@spuerhund/shared/auth";
import {
  findVaultTemplateMatches,
  resolveVaultTemplateRecord,
} from "@spuerhund/shared/vault";
import {
  DEFAULT_QUERY_RISK_LEVEL,
  buildRestPath,
  normalizeQueryRiskLevel,
  normalizeRemarks,
  normalizeRestPathAndParams,
} from "@spuerhund/shared/queryMetadata";
import { defaultQueryOperationConfig, defaultServerConnectionConfig, normalizeRequestType, normalizeServerKind } from "@spuerhund/shared/targets";
import type {
  RuntimeVaultValue,
  RuntimeWorkspaceQuery,
  RuntimeWorkspaceServer,
  RuntimeWorkspaceSnapshot,
  WorkspaceMode,
} from "@spuerhund/shared/runtime";
import * as db from "./persistence";
import { buildRestRequestUrl, resolveServerUrls } from "./serverUrls";
import { toast } from "./components/Toasts";
import {
  getCachedToken,
  setCachedToken,
  invalidateToken,
  executeTokenScript,
} from "./tokenCache";
import {
  createSharedConfig,
  deleteSharedConfig,
  getSharedConfig,
  listSharedConfigMembers,
  updateSharedConfig,
  type SharedConfigDocument,
} from "./sharedConfigClient";
import {
  exportSecretsEncrypted,
  importSecretsEncrypted,
} from "./sharedCrypto";
import { interpolateSecrets } from "./secretInterpolation";
import { mongoShellToEjson } from "./editor/lang-mongo/transform";
import {
  browserVaultProvider,
  createVault,
  exportVaultData as exportVaultSnapshot,
  forgetRememberedVault,
  importVaultData as importVaultSnapshot,
  lockVault as lockBrowserVault,
  readVaultEntryValue,
  refreshVaultSnapshot,
  removeVaultEntry,
  restoreRememberedVault,
  saveVaultEntry,
  unlockVault,
} from "./vault";
import {
  RuntimeClientError,
  executeRuntimeGraphql,
  executeRuntimeMongo,
  executeRuntimeRest,
  executeRuntimeSql,
  getRuntimeHealth,
  hasRuntimePairingToken,
  refreshRuntimeCatalog,
  syncRuntimeWorkspaceSnapshot,
} from "./runtimeClient";
import { createExecutionAbortController, withExecutionTimeoutSignal } from "./executionTimeout";
import type {
  LegacySpuerhundExport,
  StoredServer,
  StoredQuery,
  StoredSchemaCache,
  StoredHistoryEntry,
  SpuerhundExport,
  SpuerhundVaultExport,
  RequestType,
  RestMethod,
  RestContentType,
  StoredVaultEntry,
  QuerySystemPreset,
} from "./persistence";
import { useAuthStore } from "./authStore";
import type {
  SecretExportBundle,
  SharedConfigMember,
  SharedConfigPayload,
  SharedConfigPayloadQuery,
  SharedConfigRequiredVaultRef,
  SharedConfigRole,
  SharedConfigValue,
  StoredSecret,
} from "./sharedTypes";
import {
  getSharedPayloadSharedConfigs,
  getSharedPayloadRequiredVaultRefs,
  parseAuthConfig,
  resolveSharedPayloadServer,
  toSharedPayload,
} from "./sharedTypes";
import {
  payloadQueriesEqual,
  payloadServersEqual,
  storedQueryToPayloadQuery,
  type QueryDraftSnapshot,
  payloadQueryToDraft,
  toQueryDraftSnapshot,
} from "./sharedSync";
import {
  syncServerToD1,
  syncDeleteServerFromD1,
  syncQueryToD1,
  syncDeleteQueryFromD1,
  syncVaultEntryToD1,
  syncDeleteVaultEntryFromD1,
} from "./syncBridge";
import { dedupePendingVaultDrafts, findMissingVaultRefDrafts, type PendingVaultRefDraftLike } from "./sharedVault";
import {
  readStoredWorkspaceMode,
  persistWorkspaceMode,
} from "./workspaceMode";
import {
  restoreEditorStateFromHistory,
  restoreEditorStateFromQuery,
  restoreResultStateFromHistory,
} from "./historyReplay";
import type { QueryExecutionResult } from "./resultTypes";
import { RUNTIME_OBSERVER_CHANNELS, type RuntimeObserverEvent } from "./runtimeObserverClient";

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function formatIntrospectError(error: unknown): string {
  const msg = errorMessage(error);
  if (msg) return msg;
  const ctor = (error as { constructor?: { name?: string } } | null)?.constructor?.name ?? "Error";
  return `${ctor} (no message — see console)`;
}

function isMissingQueryError(error: unknown): boolean {
  return error instanceof Error && /^Query .+ not found$/.test(error.message);
}

let lastVaultLockedNoticeAt = 0;
const VAULT_LOCKED_RUNTIME_MESSAGE = "Vault is locked. Unlock it in Secrets & Config.";
const ADHOC_GQL_PRESET: QuerySystemPreset = "adhoc-gql";
const ADHOC_REST_PRESET: QuerySystemPreset = "adhoc-rest";
const ADHOC_MONGO_PRESET: QuerySystemPreset = "adhoc-mongo";
const ADHOC_SQL_PRESET: QuerySystemPreset = "adhoc-sql";
const DEFAULT_SAVED_CATEGORY = "Saved";
const SHARED_DISPLAY_NAME_KEY = "spuerhund:shared-display-name";
const AUTO_PUSH_DEBOUNCE_MS = 1_500;
const RUNTIME_WORKSPACE_SYNC_DEBOUNCE_MS = 1_000;

const autoPushTimers = new Map<string, number>();
let runtimeWorkspaceSyncTimer: number | null = null;

type PendingVaultRefDraft = PendingVaultRefDraftLike;

class MissingVaultConfigError extends Error {
  readonly keys: string[];
  constructor(keys: string[]) {
    super(`Missing vault config: ${keys.join(", ")}`);
    this.name = "MissingVaultConfigError";
    this.keys = keys;
  }
}

function shouldUseRuntime(): boolean {
  const runtime = useRuntimeStore.getState();
  return runtime.reachable && runtime.paired;
}

function handleRuntimeSetupError(error: unknown): string {
  const message = errorMessage(error);

  if (error instanceof RuntimeClientError) {
    if (error.code === "PAIRING_REQUIRED") {
      useRuntimeStore.getState().setSnapshot({
        reachable: true,
        paired: false,
        version: "",
        agentProviders: [],
        checking: false,
      });
      useAppStore.getState().setShowRuntimeDialog(true);
    }

    if (error.code === "RUNTIME_UNREACHABLE") {
      useRuntimeStore.getState().setSnapshot({
        reachable: false,
        paired: false,
        version: "",
        agentProviders: [],
        checking: false,
      });
    }
  }

  if (message === "Vault is locked") {
    useAppStore.getState().setShowVaultManager(true);

    const now = Date.now();
    if (now - lastVaultLockedNoticeAt > 1_500) {
      toast(VAULT_LOCKED_RUNTIME_MESSAGE, "error");
      lastVaultLockedNoticeAt = now;
    }

    return VAULT_LOCKED_RUNTIME_MESSAGE;
  }

  return message;
}

function readSharedDisplayName(): string | null {
  const value = localStorage.getItem(SHARED_DISPLAY_NAME_KEY)?.trim();
  return value ? value : null;
}

function setSharedDisplayName(displayName: string) {
  localStorage.setItem(SHARED_DISPLAY_NAME_KEY, displayName);
}

function ensureSharedDisplayName(): string {
  const existing = readSharedDisplayName();
  if (existing) return existing;

  const authUser = useAuthStore.getState().user;
  if (authUser?.displayName) {
    setSharedDisplayName(authUser.displayName);
    return authUser.displayName;
  }

  if (authUser?.email) {
    setSharedDisplayName(authUser.email);
    return authUser.email;
  }

  throw new Error("Sign in to share or connect servers");
}

function extractScriptVaultRefs(script: string): SharedConfigRequiredVaultRef[] {
  const refs: SharedConfigRequiredVaultRef[] = [];
  const pattern = /\b(secret|config)\(\s*["']([^"']+)["']\s*\)/g;

  for (const match of script.matchAll(pattern)) {
    const kind = match[1];
    const key = match[2]?.trim();
    if ((kind === "secret" || kind === "config") && key) {
      refs.push({ kind, key });
    }
  }

  return refs;
}

function extractTemplateVaultRefs(value: string): SharedConfigRequiredVaultRef[] {
  return findVaultTemplateMatches(value).map((match) => ({
    kind: match.kind,
    key: match.key,
  }));
}

function collectStringVaultRefs(value: string): SharedConfigRequiredVaultRef[] {
  return [
    ...extractTemplateVaultRefs(value),
    ...extractScriptVaultRefs(value),
  ];
}

function collectJsonVaultRefs(value: unknown): SharedConfigRequiredVaultRef[] {
  return value == null ? [] : collectStringVaultRefs(JSON.stringify(value));
}

function defaultAdHocPresetForServer(server: Pick<StoredServer, "serverKind"> | undefined): QuerySystemPreset {
  switch (normalizeServerKind(server?.serverKind)) {
    case "mongo":
      return ADHOC_MONGO_PRESET;
    case "postgres":
      return ADHOC_SQL_PRESET;
    default:
      return ADHOC_GQL_PRESET;
  }
}

function buildMissingAdHocPresets(
  server: Pick<StoredServer, "id" | "serverKind">,
  queries: StoredQuery[],
): Array<Parameters<typeof db.createQuery>[0]> {
  const presets: Array<Parameters<typeof db.createQuery>[0]> = [];
  const hasPreset = (preset: QuerySystemPreset) => Boolean(findQueryByPreset(queries, preset));
  const serverKind = normalizeServerKind(server.serverKind);

  if (serverKind === "http") {
    if (!hasPreset(ADHOC_GQL_PRESET)) {
      presets.push({
        id: `${server.id}:${ADHOC_GQL_PRESET}`,
        serverId: server.id,
        name: "AdHoc GQL",
        systemPreset: ADHOC_GQL_PRESET,
        requestType: "graphql",
        queryText: "",
        variables: "{}",
        headers: "{}",
      });
    }
    if (!hasPreset(ADHOC_REST_PRESET)) {
      presets.push({
        id: `${server.id}:${ADHOC_REST_PRESET}`,
        serverId: server.id,
        name: "AdHoc REST",
        systemPreset: ADHOC_REST_PRESET,
        requestType: "rest",
        restMethod: "GET",
        restPath: "/",
        restContentType: "json",
        queryText: "",
        variables: "{}",
        headers: "{}",
      });
    }
    return presets;
  }

  if (serverKind === "mongo" && !hasPreset(ADHOC_MONGO_PRESET)) {
    presets.push({
      id: `${server.id}:${ADHOC_MONGO_PRESET}`,
      serverId: server.id,
      name: "AdHoc Mongo",
      systemPreset: ADHOC_MONGO_PRESET,
      requestType: "mongo",
      operationConfig: {
        kind: "mongo",
        collection: "",
        operation: "find",
      },
      queryText: "{}",
      variables: "{}",
      headers: "{}",
    });
  }

  if (serverKind === "postgres" && !hasPreset(ADHOC_SQL_PRESET)) {
    presets.push({
      id: `${server.id}:${ADHOC_SQL_PRESET}`,
      serverId: server.id,
      name: "AdHoc SQL",
      systemPreset: ADHOC_SQL_PRESET,
      requestType: "sql",
      operationConfig: {
        kind: "sql",
      },
      queryText: "",
      variables: "[]",
      headers: "{}",
    });
  }

  return presets;
}

export async function collectRuntimeVaultValues(
  server: StoredServer,
  input: {
    rawHeaders: string;
    rawVariables?: string;
    rawQueryText?: string;
    rawRestPath?: string;
    operationConfig?: StoredQuery["operationConfig"];
  },
): Promise<RuntimeVaultValue[]> {
  const refs = new Map<string, SharedConfigRequiredVaultRef>();
  const addRefs = (items: SharedConfigRequiredVaultRef[]) => {
    for (const item of items) {
      refs.set(`${item.kind}:${item.key}`, item);
    }
  };

  addRefs(collectStringVaultRefs(server.baseUrl));
  if (server.graphqlPath) addRefs(collectStringVaultRefs(server.graphqlPath));
  if (server.restPath) addRefs(collectStringVaultRefs(server.restPath));
  addRefs(collectJsonVaultRefs(server.connectionConfig));
  addRefs(collectStringVaultRefs(server.authConfig));
  addRefs(collectStringVaultRefs(input.rawHeaders));
  if (input.rawVariables) addRefs(collectStringVaultRefs(input.rawVariables));
  if (input.rawQueryText) addRefs(collectStringVaultRefs(input.rawQueryText));
  if (input.rawRestPath) addRefs(collectStringVaultRefs(input.rawRestPath));
  addRefs(collectJsonVaultRefs(input.operationConfig));

  if (refs.size === 0) {
    return [];
  }

  const values: RuntimeVaultValue[] = [];
  for (const ref of refs.values()) {
    values.push({
      kind: ref.kind,
      key: ref.key,
      value: await browserVaultProvider.resolveValue(ref.kind, ref.key, { serverId: server.id }),
    });
  }

  return values;
}

async function collectRuntimeWorkspaceVaultValues(
  server: StoredServer,
  queries: StoredQuery[],
): Promise<RuntimeVaultValue[]> {
  const refs = new Map<string, SharedConfigRequiredVaultRef>();
  const addRefs = (items: SharedConfigRequiredVaultRef[]) => {
    for (const item of items) {
      refs.set(`${item.kind}:${item.key}`, item);
    }
  };

  addRefs(collectStringVaultRefs(server.baseUrl));
  if (server.graphqlPath) addRefs(collectStringVaultRefs(server.graphqlPath));
  if (server.restPath) addRefs(collectStringVaultRefs(server.restPath));
  addRefs(collectJsonVaultRefs(server.connectionConfig));
  addRefs(collectStringVaultRefs(server.authConfig));

  for (const query of queries) {
    addRefs(collectStringVaultRefs(query.headers));
    addRefs(collectStringVaultRefs(query.variables));
    if (query.restPath) addRefs(collectStringVaultRefs(query.restPath));
    addRefs(collectJsonVaultRefs(query.operationConfig));
  }

  const values: RuntimeVaultValue[] = [];
  for (const ref of refs.values()) {
    values.push({
      kind: ref.kind,
      key: ref.key,
      value: await browserVaultProvider.resolveValue(ref.kind, ref.key, { serverId: server.id }),
    });
  }
  return values;
}

function toRuntimeWorkspaceQuery(query: StoredQuery): RuntimeWorkspaceQuery {
  const normalizedRest = normalizeRestPathAndParams(query.restPath, query.restParams);
  return {
    id: query.id,
    name: query.name,
    queryText: query.queryText,
    variables: query.variables,
    headers: query.headers,
    remarks: normalizeRemarks(query.remarks),
    riskLevel: normalizeQueryRiskLevel(query.riskLevel),
    ...(query.category ? { category: query.category } : {}),
    ...(query.pinnedAt !== undefined ? { pinnedAt: query.pinnedAt } : {}),
    requestType: normalizeRequestType(query.requestType),
    ...(query.operationConfig || query.requestType === "mongo" || query.requestType === "sql"
      ? { operationConfig: defaultQueryOperationConfig(query) ?? query.operationConfig }
      : {}),
    ...(query.restMethod ? { restMethod: query.restMethod } : {}),
    ...(normalizedRest.restPath != null ? { restPath: normalizedRest.restPath } : {}),
    ...(normalizedRest.restParams.length > 0 ? { restParams: normalizedRest.restParams } : {}),
    ...(query.restContentType ? { restContentType: query.restContentType } : {}),
  };
}

async function buildRuntimeWorkspaceSnapshot(): Promise<RuntimeWorkspaceSnapshot> {
  const mode = useAppStore.getState().mode;
  const servers = mode === "signed-in"
    ? useServersStore.getState().servers
    : [];
  const runtimeServers: RuntimeWorkspaceServer[] = [];

  for (const server of servers) {
    const queries = overlayLocalPaneDrafts(
      server.id,
      (await db.listQueries(server.id)).filter((query) => !isAdHocQuery(query)),
    );
    runtimeServers.push({
      id: server.id,
      name: server.name,
      serverKind: normalizeServerKind(server.serverKind),
      connectionConfig: defaultServerConnectionConfig(server),
      baseUrl: server.baseUrl,
      authType: server.authType,
      authConfig: server.authConfig,
      ...(server.graphqlPath != null ? { graphqlPath: server.graphqlPath } : {}),
      ...(server.restPath != null ? { restPath: server.restPath } : {}),
      ...(server.isProduction !== undefined ? { isProduction: server.isProduction } : {}),
      ...(server.timeoutMs != null ? { timeoutMs: server.timeoutMs } : {}),
      ...(server.sharedConfigId ? { sharedConfigId: server.sharedConfigId } : {}),
      queries: queries.map(toRuntimeWorkspaceQuery),
      vaultValues: await collectRuntimeWorkspaceVaultValues(server, queries),
    });
  }

  return {
    schemaVersion: 2,
    mode,
    syncedAt: new Date().toISOString(),
    servers: runtimeServers,
  };
}

async function syncRuntimeWorkspaceSnapshotFromApp() {
  if (!shouldUseRuntime()) {
    return;
  }

  try {
    await pushRuntimeWorkspaceSnapshotNow();
  } catch (error) {
    handleRuntimeSetupError(error);
  }
}

async function pushRuntimeWorkspaceSnapshotNow() {
  const snapshot = await buildRuntimeWorkspaceSnapshot();
  await syncRuntimeWorkspaceSnapshot(snapshot);
}

export function scheduleRuntimeWorkspaceSync() {
  if (runtimeWorkspaceSyncTimer !== null) {
    window.clearTimeout(runtimeWorkspaceSyncTimer);
  }
  runtimeWorkspaceSyncTimer = window.setTimeout(() => {
    runtimeWorkspaceSyncTimer = null;
    void syncRuntimeWorkspaceSnapshotFromApp();
  }, RUNTIME_WORKSPACE_SYNC_DEBOUNCE_MS);
}

function findInvalidVaultPlaceholders(value: string): string[] {
  const invalid = new Set<string>();

  for (const match of value.matchAll(/{{([^}]+)}}/g)) {
    const raw = match[1]?.trim() ?? "";
    if (!/^(secret|config)\s*:\s*.+$/i.test(raw)) {
      invalid.add(raw || match[0]);
    }
  }

  return [...invalid];
}

// ── Servers Store ────────────────────────────────────────────────────

interface ServersState {
  servers: StoredServer[];
  activeServerId: string | null;
  setServers: (servers: StoredServer[]) => void;
  setActiveServerId: (id: string | null) => void;
}

export const useServersStore = create<ServersState>((set) => ({
  servers: [],
  activeServerId: null,
  setServers: (servers) => set({ servers }),
  setActiveServerId: (id) => set({ activeServerId: id }),
}));

// ── Queries Store ────────────────────────────────────────────────────

interface QueriesState {
  queries: StoredQuery[];
  activeQueryId: string | null;
  setQueries: (queries: StoredQuery[]) => void;
  setActiveQueryId: (id: string | null) => void;
}

export const useQueriesStore = create<QueriesState>((set) => ({
  queries: [],
  activeQueryId: null,
  setQueries: (queries) => set({ queries }),
  setActiveQueryId: (id) => set({ activeQueryId: id }),
}));

// ── Editor Store ─────────────────────────────────────────────────────

interface EditorState {
  queryText: string;
  variables: string;
  headers: string;
  activeTab: "query" | "variables" | "headers" | "params" | "options";
  requestType: RequestType;
  restMethod: RestMethod;
  restPath: string;
  restParams: db.RestParam[];
  restContentType: RestContentType;
  setQueryText: (text: string) => void;
  setVariables: (text: string) => void;
  setHeaders: (text: string) => void;
  setActiveTab: (tab: "query" | "variables" | "headers" | "params" | "options") => void;
  setRequestType: (t: RequestType) => void;
  setRestMethod: (m: RestMethod) => void;
  setRestPath: (p: string) => void;
  setRestParams: (p: db.RestParam[]) => void;
  setRestContentType: (ct: RestContentType) => void;
}

export const useEditorStore = create<EditorState>((set) => ({
  queryText: "",
  variables: "{}",
  headers: "{}",
  activeTab: "query",
  requestType: "graphql",
  restMethod: "GET",
  restPath: "/",
  restParams: [],
  restContentType: "json",
  setQueryText: (queryText) => set({ queryText }),
  setVariables: (variables) => set({ variables }),
  setHeaders: (headers) => set({ headers }),
  setActiveTab: (activeTab) => set({ activeTab }),
  setRequestType: (requestType) => set({ requestType }),
  setRestMethod: (restMethod) => set({ restMethod }),
  setRestPath: (restPath) => set({ restPath }),
  setRestParams: (restParams) => set({ restParams }),
  setRestContentType: (restContentType) => set({ restContentType }),
}));

// ── Result Store ─────────────────────────────────────────────────────

type ConnectionStatus = "unknown" | "connected" | "error";

interface ResultState {
  result: QueryExecutionResult | null;
  isExecuting: boolean;
  abortController: AbortController | null;
  executionStartedAt: number | null;
  error: string | null;
  connectionStatus: ConnectionStatus;
  setResult: (r: ResultState["result"]) => void;
  setIsExecuting: (v: boolean) => void;
  setAbortController: (c: AbortController | null) => void;
  setExecutionStartedAt: (t: number | null) => void;
  setError: (e: string | null) => void;
  setConnectionStatus: (status: ConnectionStatus) => void;
}

export const useResultStore = create<ResultState>((set) => ({
  result: null,
  isExecuting: false,
  abortController: null,
  executionStartedAt: null,
  error: null,
  connectionStatus: "unknown",
  setResult: (result) => set({ result, error: null }),
  setIsExecuting: (isExecuting) => set({ isExecuting }),
  setAbortController: (abortController) => set({ abortController }),
  setExecutionStartedAt: (executionStartedAt) => set({ executionStartedAt }),
  setError: (error) => set({ error }),
  setConnectionStatus: (connectionStatus) => set({ connectionStatus }),
}));

export function cancelQuery() {
  const { abortController } = useResultStore.getState();
  if (abortController) abortController.abort();
}

// ── Execution Tracking Store ─────────────────────────────────────────
// Tracks which query IDs are currently executing across all panes.

interface ExecutionTrackingState {
  executingQueryIds: Set<string>;
  markExecuting: (queryId: string) => void;
  markDone: (queryId: string) => void;
}

export const useExecutionTrackingStore = create<ExecutionTrackingState>((set) => ({
  executingQueryIds: new Set(),
  markExecuting: (queryId) =>
    set((state) => {
      const next = new Set(state.executingQueryIds);
      next.add(queryId);
      return { executingQueryIds: next };
    }),
  markDone: (queryId) =>
    set((state) => {
      const next = new Set(state.executingQueryIds);
      next.delete(queryId);
      return { executingQueryIds: next };
    }),
}));

// ── Schema Store ─────────────────────────────────────────────────────

interface SchemaState {
  schemaCache: Record<string, StoredSchemaCache>;
  setSchemaCache: (serverId: string, schema: StoredSchemaCache) => void;
}

export const useSchemaStore = create<SchemaState>((set) => ({
  schemaCache: {},
  setSchemaCache: (serverId, schema) =>
    set((state) => ({
      schemaCache: { ...state.schemaCache, [serverId]: schema },
    })),
}));

// ── History Store ────────────────────────────────────────────────────

interface HistoryState {
  entries: StoredHistoryEntry[];
  setEntries: (entries: StoredHistoryEntry[]) => void;
}

export const useHistoryStore = create<HistoryState>((set) => ({
  entries: [],
  setEntries: (entries) => set({ entries }),
}));

// ── Vault Store ──────────────────────────────────────────────────────

interface VaultState {
  hasVault: boolean;
  isUnlocked: boolean;
  isRemembered: boolean;
  entries: StoredVaultEntry[];
  setSnapshot: (snapshot: { hasVault: boolean; isUnlocked: boolean; isRemembered: boolean; entries: StoredVaultEntry[] }) => void;
}

export const useVaultStore = create<VaultState>((set) => ({
  hasVault: false,
  isUnlocked: false,
  isRemembered: false,
  entries: [],
  setSnapshot: (snapshot) => set(snapshot),
}));

// ── Runtime Store ───────────────────────────────────────────────────

interface RuntimeState {
  reachable: boolean;
  paired: boolean;
  version: string;
  agentProviders: string[];
  checking: boolean;
  setSnapshot: (snapshot: {
    reachable: boolean;
    paired: boolean;
    version: string;
    agentProviders: string[];
    checking: boolean;
  }) => void;
}

export const useRuntimeStore = create<RuntimeState>((set) => ({
  reachable: false,
  paired: false,
  version: "",
  agentProviders: [],
  checking: false,
  setSnapshot: (snapshot) => set(snapshot),
}));

// ── Observer Store ──────────────────────────────────────────────────

export type ObserverExecutionRecord = {
  executionId: string;
  sessionId: string;
  source?: "human-ui" | "cli" | "mcp";
  initiator?: "human-ui" | "cli" | "mcp";
  requestType?: "graphql" | "rest" | "mongo" | "sql";
  intent?: "read" | "write" | "unknown";
  approvalMode?: "none" | "required";
  status: "queued" | "pending-approval" | "approved" | "started" | "completed" | "failed" | "rejected" | "timed-out";
  updatedAt: number;
  statusCode?: number;
  durationMs?: number;
  reason?: string;
  observerId?: string;
  errorMessage?: string;
};

interface ObserverState {
  connected: boolean;
  sessions: Record<string, ObserverExecutionRecord[]>;
  setConnected: (connected: boolean) => void;
  applyRuntimeObserverEvent: (event: RuntimeObserverEvent) => void;
  setApprovalHandlers: (
    handlers: {
      approveExecution: (executionId: string) => void;
      rejectExecution: (executionId: string, reason: string) => void;
    } | null,
  ) => void;
  approveExecution: (executionId: string) => void;
  rejectExecution: (executionId: string, reason: string) => void;
}

function normalizeObserverSessionId(sessionId: string | undefined, executionId: string): string {
  const normalized = sessionId?.trim();
  return normalized ? normalized : executionId;
}

function upsertObserverExecution(
  sessions: Record<string, ObserverExecutionRecord[]>,
  nextExecution: ObserverExecutionRecord,
): Record<string, ObserverExecutionRecord[]> {
  const nextSessions: Record<string, ObserverExecutionRecord[]> = {};
  let existingExecution: ObserverExecutionRecord | null = null;

  for (const [sessionId, executions] of Object.entries(sessions)) {
    const remainingExecutions = executions.filter((execution) => {
      if (execution.executionId !== nextExecution.executionId) {
        return true;
      }

      existingExecution = execution;
      return false;
    });

    if (remainingExecutions.length > 0) {
      nextSessions[sessionId] = remainingExecutions;
    }
  }

  let mergedExecution: ObserverExecutionRecord;
  if (existingExecution) {
    mergedExecution = Object.assign({}, existingExecution, nextExecution);
  } else {
    mergedExecution = nextExecution;
  }
  const sessionId = normalizeObserverSessionId(mergedExecution.sessionId, mergedExecution.executionId);

  nextSessions[sessionId] = [...(nextSessions[sessionId] ?? []), { ...mergedExecution, sessionId }]
    .sort((left, right) => right.updatedAt - left.updatedAt);

  return nextSessions;
}

function findObserverExecution(
  sessions: Record<string, ObserverExecutionRecord[]>,
  executionId: string,
): ObserverExecutionRecord | null {
  for (const executions of Object.values(sessions)) {
    const match = executions.find((execution) => execution.executionId === executionId);
    if (match) return match;
  }

  return null;
}

function applyObserverPushEvent(
  sessions: Record<string, ObserverExecutionRecord[]>,
  event: RuntimeObserverEvent,
): Record<string, ObserverExecutionRecord[]> {
  const updatedAt = Date.now();

  switch (event.channel) {
    case RUNTIME_OBSERVER_CHANNELS.executionQueued:
      return upsertObserverExecution(sessions, {
        executionId: event.data.executionId,
        sessionId: normalizeObserverSessionId(event.data.sessionId, event.data.executionId),
        source: event.data.source,
        initiator: event.data.initiator,
        requestType: event.data.requestType,
        intent: event.data.intent,
        approvalMode: event.data.approvalMode,
        status: "queued",
        updatedAt,
      });

    case RUNTIME_OBSERVER_CHANNELS.approvalRequested:
      return upsertObserverExecution(sessions, {
        executionId: event.data.executionId,
        sessionId: normalizeObserverSessionId(event.data.sessionId, event.data.executionId),
        source: event.data.source,
        initiator: event.data.initiator,
        requestType: event.data.requestType,
        intent: event.data.intent,
        approvalMode: event.data.approvalMode,
        status: "pending-approval",
        updatedAt,
      });

    case RUNTIME_OBSERVER_CHANNELS.approvalResolved:
      return upsertObserverExecution(sessions, {
        executionId: event.data.executionId,
        sessionId: normalizeObserverSessionId(event.data.sessionId, event.data.executionId),
        source: event.data.source,
        initiator: event.data.initiator,
        requestType: event.data.requestType,
        intent: event.data.intent,
        approvalMode: event.data.approvalMode,
        status:
          event.data.decision === "approved"
            ? "approved"
            : event.data.decision === "rejected"
              ? "rejected"
              : "timed-out",
        updatedAt,
        ...("reason" in event.data && event.data.reason ? { reason: event.data.reason } : {}),
        ...("observerId" in event.data ? { observerId: event.data.observerId } : {}),
      });

    case RUNTIME_OBSERVER_CHANNELS.executionStarted:
      return upsertObserverExecution(sessions, {
        executionId: event.data.executionId,
        sessionId: normalizeObserverSessionId(event.data.sessionId, event.data.executionId),
        status: "started",
        updatedAt,
      });

    case RUNTIME_OBSERVER_CHANNELS.executionCompleted:
      return upsertObserverExecution(sessions, {
        executionId: event.data.executionId,
        sessionId: normalizeObserverSessionId(event.data.sessionId, event.data.executionId),
        status: "completed",
        updatedAt,
        statusCode: event.data.statusCode,
        durationMs: event.data.durationMs,
      });

    case RUNTIME_OBSERVER_CHANNELS.executionFailed:
      const existingExecution = findObserverExecution(sessions, event.data.executionId);
      return upsertObserverExecution(sessions, {
        executionId: event.data.executionId,
        sessionId: normalizeObserverSessionId(event.data.sessionId, event.data.executionId),
        status: existingExecution?.status === "rejected" || existingExecution?.status === "timed-out"
          ? existingExecution.status
          : "failed",
        updatedAt,
        errorMessage: event.data.errorMessage,
      });

    default:
      return sessions;
  }
}

export const useObserverStore = create<ObserverState>((set) => {
  let approvalHandlers: {
    approveExecution: (executionId: string) => void;
    rejectExecution: (executionId: string, reason: string) => void;
  } | null = null;

  return {
    connected: false,
    sessions: {},
    setConnected: (connected) => set({ connected }),
    applyRuntimeObserverEvent: (event) => set((state) => ({
      sessions: applyObserverPushEvent(state.sessions, event),
    })),
    setApprovalHandlers: (handlers) => {
      approvalHandlers = handlers;
    },
    approveExecution: (executionId) => {
      approvalHandlers?.approveExecution(executionId);
    },
    rejectExecution: (executionId, reason) => {
      approvalHandlers?.rejectExecution(executionId, reason);
    },
  };
});

// ── Secret Registry Store ─────────────────────────────────────────────

interface SecretsState {
  secrets: StoredSecret[];
  setSecrets: (secrets: StoredSecret[]) => void;
}

export const useSecretsStore = create<SecretsState>((set) => ({
  secrets: [],
  setSecrets: (secrets) => set({ secrets }),
}));

// ── HTTP Debug Store ─────────────────────────────────────────────────

export interface HttpDebugInfo {
  request: {
    method: string;
    url: string;
    headers: Record<string, string>;
    body?: string;
  };
  response?: {
    status: number;
    statusText: string;
    headers: Record<string, string>;
    body?: string;
  };
  error?: string;
  durationMs?: number;
}

interface HttpDebugState {
  debug: HttpDebugInfo | null;
  setDebug: (info: HttpDebugInfo) => void;
  clear: () => void;
}

export const useHttpDebugStore = create<HttpDebugState>((set) => ({
  debug: null,
  setDebug: (debug) => set({ debug }),
  clear: () => set({ debug: null }),
}));

function responseHeaders(response: Response): Record<string, string> {
  const h: Record<string, string> = {};
  response.headers.forEach((v, k) => { h[k] = v; });
  return h;
}

// ── App UI Store ─────────────────────────────────────────────────────

const MAX_SECONDARY_PANES = 3;
export type QueryPaneId = "primary" | string;

export interface SecondaryPaneState {
  id: string;
  queryId: string;
}

interface AppState {
  mode: WorkspaceMode;
  setMode: (mode: WorkspaceMode) => void;
  showQuickOpen: boolean;
  setShowQuickOpen: (v: boolean) => void;
  showSchemaBrowser: boolean;
  setShowSchemaBrowser: (v: boolean) => void;
  showVaultManager: boolean;
  setShowVaultManager: (v: boolean) => void;
  showRuntimeDialog: boolean;
  setShowRuntimeDialog: (v: boolean) => void;
  showLocalAppsDialog: boolean;
  setShowLocalAppsDialog: (v: boolean) => void;
  importPreview: import("./importExternal").ExternalImportResult | null;
  setImportPreview: (v: import("./importExternal").ExternalImportResult | null) => void;
  pendingVaultRefs: PendingVaultRefDraft[];
  setPendingVaultRefs: (refs: PendingVaultRefDraft[]) => void;
  clearPendingVaultRefs: () => void;
  activePaneId: QueryPaneId;
  setActivePaneId: (paneId: QueryPaneId) => void;
  secondaryPanels: SecondaryPaneState[];
  openSecondaryPane: (queryId: string) => void;
  assignQueryToPane: (paneId: string, queryId: string) => void;
  closeSecondaryPane: (paneId: string) => void;
  closePanesForQuery: (queryId: string) => void;
  closeAllSecondaryPanes: () => void;
}

export const useAppStore = create<AppState>((set, get) => ({
  mode: readStoredWorkspaceMode(),
  setMode: (mode) => {
    persistWorkspaceMode(mode);
    set({ mode });
  },
  showQuickOpen: false,
  setShowQuickOpen: (showQuickOpen) => set({ showQuickOpen }),
  showSchemaBrowser: false,
  setShowSchemaBrowser: (showSchemaBrowser) => set({ showSchemaBrowser }),
  showVaultManager: false,
  setShowVaultManager: (showVaultManager) => set({ showVaultManager }),
  showRuntimeDialog: false,
  setShowRuntimeDialog: (showRuntimeDialog) => set({ showRuntimeDialog }),
  showLocalAppsDialog: false,
  setShowLocalAppsDialog: (showLocalAppsDialog) => set({ showLocalAppsDialog }),
  importPreview: null,
  setImportPreview: (importPreview) => set({ importPreview }),
  pendingVaultRefs: [],
  setPendingVaultRefs: (pendingVaultRefs) => set({ pendingVaultRefs }),
  clearPendingVaultRefs: () => set({ pendingVaultRefs: [] }),
  activePaneId: "primary",
  setActivePaneId: (activePaneId) => set({ activePaneId }),
  secondaryPanels: [],
  openSecondaryPane: (queryId) => {
    const { secondaryPanels } = get();
    if (secondaryPanels.length >= MAX_SECONDARY_PANES) {
      toast(`Maximum ${MAX_SECONDARY_PANES} secondary panes`, "error");
      return;
    }
    const paneId = crypto.randomUUID();
    set({
      secondaryPanels: [...secondaryPanels, { id: paneId, queryId }],
      activePaneId: paneId,
    });
  },
  assignQueryToPane: (paneId, queryId) => {
    set((state) => ({
      secondaryPanels: state.secondaryPanels.map((pane) =>
        pane.id === paneId ? { ...pane, queryId } : pane,
      ),
    }));
  },
  closeSecondaryPane: (paneId) => {
    set((state) => {
      const closingIndex = state.secondaryPanels.findIndex((pane) => pane.id === paneId);
      if (closingIndex === -1) return state;

      const nextPanels = state.secondaryPanels.filter((pane) => pane.id !== paneId);
      let nextActivePaneId = state.activePaneId;

      if (state.activePaneId === paneId) {
        nextActivePaneId =
          nextPanels[Math.max(0, closingIndex - 1)]?.id
          ?? nextPanels[0]?.id
          ?? "primary";
      }

      return {
        secondaryPanels: nextPanels,
        activePaneId: nextActivePaneId,
      };
    });
  },
  closePanesForQuery: (queryId) => {
    set((state) => {
      const nextPanels = state.secondaryPanels.filter((pane) => pane.queryId !== queryId);
      if (nextPanels.length === state.secondaryPanels.length) return state;

      const activePaneStillExists = state.activePaneId === "primary"
        || nextPanels.some((pane) => pane.id === state.activePaneId);

      return {
        secondaryPanels: nextPanels,
        activePaneId: activePaneStillExists ? state.activePaneId : "primary",
      };
    });
  },
  closeAllSecondaryPanes: () => set({ secondaryPanels: [], activePaneId: "primary" }),
}));

// ── Query History Navigation Store ──────────────────────────────────

interface QueryHistoryNavState {
  queryHistory: db.StoredHistoryEntry[];
  historyCursor: number | null; // null = live, 0 = newest history, ascending = older
  setQueryHistory: (entries: db.StoredHistoryEntry[]) => void;
  setHistoryCursor: (cursor: number | null) => void;
}

export const useQueryHistoryNavStore = create<QueryHistoryNavState>((set) => ({
  queryHistory: [],
  historyCursor: null,
  setQueryHistory: (queryHistory) => set({ queryHistory }),
  setHistoryCursor: (historyCursor) => set({ historyCursor }),
}));

// ── Shared Sync Store ───────────────────────────────────────────────

export type SharedServerConnectionState = "idle" | "connecting" | "live" | "offline";

export interface SharedServerSyncState {
  connection: SharedServerConnectionState;
  pushing: boolean;
  hasServerConflict: boolean;
  lastRemoteUpdatedAt?: string;
  lastRemoteActor?: string | null;
  recentRemoteQueryIds: string[];
}

export interface SharedPaneDraftState {
  paneId: string;
  serverId: string;
  queryId: string;
  dirty: boolean;
  touchedAt: number;
  snapshot: QueryDraftSnapshot;
}

export interface SharedQueryConflictState {
  queryId: string;
  serverId: string;
  queryName: string;
  updatedAt: string;
  updatedByClientId: string;
  updatedByDisplayName?: string | null;
  remoteDeleted: boolean;
}

export interface SharedFooterEvent {
  id: number;
  serverId: string;
  tone: "info" | "success" | "error";
  message: string;
  createdAt: number;
}

interface SharedSyncState {
  servers: Record<string, SharedServerSyncState>;
  paneDrafts: Record<string, SharedPaneDraftState>;
  querySyncedSnapshots: Record<string, QueryDraftSnapshot>;
  queryConflicts: Record<string, SharedQueryConflictState>;
  recentRemoteUpdates: Record<string, number>;
  footerEvents: SharedFooterEvent[];
  setServerConnection: (serverId: string, connection: SharedServerConnectionState) => void;
  setServerPushing: (serverId: string, pushing: boolean) => void;
  setServerRemoteInfo: (serverId: string, info: { updatedAt: string; actor?: string | null; queryIds?: string[] }) => void;
  setServerConflict: (serverId: string, hasServerConflict: boolean) => void;
  setPaneDraft: (draft: SharedPaneDraftState) => void;
  clearPaneDraft: (paneId: string) => void;
  setQuerySyncedSnapshots: (snapshots: Record<string, QueryDraftSnapshot>) => void;
  clearQuerySyncedSnapshots: (queryIds: string[]) => void;
  upsertQueryConflict: (conflict: SharedQueryConflictState) => void;
  clearQueryConflict: (queryId: string) => void;
  clearServerConflicts: (serverId: string, keepQueryIds?: string[]) => void;
  markRecentRemoteUpdates: (queryIds: string[]) => void;
  pushFooterEvent: (event: Omit<SharedFooterEvent, "id" | "createdAt">) => void;
  pruneFooterEvents: () => void;
  pruneRecentRemoteUpdates: () => void;
}

let nextSharedFooterEventId = 0;

function defaultSharedServerSyncState(): SharedServerSyncState {
  return {
    connection: "idle",
    pushing: false,
    hasServerConflict: false,
    recentRemoteQueryIds: [],
  };
}

export const useSharedSyncStore = create<SharedSyncState>((set) => ({
  servers: {},
  paneDrafts: {},
  querySyncedSnapshots: {},
  queryConflicts: {},
  recentRemoteUpdates: {},
  footerEvents: [],
  setServerConnection: (serverId, connection) => set((state) => ({
    servers: {
      ...state.servers,
      [serverId]: {
        ...(state.servers[serverId] ?? defaultSharedServerSyncState()),
        connection,
      },
    },
  })),
  setServerPushing: (serverId, pushing) => set((state) => ({
    servers: {
      ...state.servers,
      [serverId]: {
        ...(state.servers[serverId] ?? defaultSharedServerSyncState()),
        pushing,
      },
    },
  })),
  setServerRemoteInfo: (serverId, info) => set((state) => ({
    servers: {
      ...state.servers,
      [serverId]: {
        ...(state.servers[serverId] ?? defaultSharedServerSyncState()),
        lastRemoteUpdatedAt: info.updatedAt,
        lastRemoteActor: info.actor ?? null,
        recentRemoteQueryIds: info.queryIds ?? (state.servers[serverId]?.recentRemoteQueryIds ?? []),
      },
    },
  })),
  setServerConflict: (serverId, hasServerConflict) => set((state) => ({
    servers: {
      ...state.servers,
      [serverId]: {
        ...(state.servers[serverId] ?? defaultSharedServerSyncState()),
        hasServerConflict,
      },
    },
  })),
  setPaneDraft: (draft) => set((state) => ({
    paneDrafts: {
      ...state.paneDrafts,
      [draft.paneId]: draft,
    },
  })),
  clearPaneDraft: (paneId) => set((state) => {
    const nextDrafts = { ...state.paneDrafts };
    delete nextDrafts[paneId];
    return { paneDrafts: nextDrafts };
  }),
  setQuerySyncedSnapshots: (snapshots) => set((state) => ({
    querySyncedSnapshots: {
      ...state.querySyncedSnapshots,
      ...snapshots,
    },
  })),
  clearQuerySyncedSnapshots: (queryIds) => set((state) => {
    if (queryIds.length === 0) return state;
    const nextSnapshots = { ...state.querySyncedSnapshots };
    for (const queryId of queryIds) {
      delete nextSnapshots[queryId];
    }
    return { querySyncedSnapshots: nextSnapshots };
  }),
  upsertQueryConflict: (conflict) => set((state) => ({
    queryConflicts: {
      ...state.queryConflicts,
      [conflict.queryId]: conflict,
    },
  })),
  clearQueryConflict: (queryId) => set((state) => {
    const nextConflicts = { ...state.queryConflicts };
    delete nextConflicts[queryId];
    return { queryConflicts: nextConflicts };
  }),
  clearServerConflicts: (serverId, keepQueryIds = []) => set((state) => {
    const keep = new Set(keepQueryIds);
    const nextConflicts = Object.fromEntries(
      Object.entries(state.queryConflicts).filter(([, conflict]) =>
        conflict.serverId !== serverId || keep.has(conflict.queryId),
      ),
    );
    return { queryConflicts: nextConflicts };
  }),
  markRecentRemoteUpdates: (queryIds) => set((state) => ({
    recentRemoteUpdates: {
      ...state.recentRemoteUpdates,
      ...Object.fromEntries(queryIds.map((queryId) => [queryId, Date.now()])),
    },
  })),
  pushFooterEvent: (event) => set((state) => ({
    footerEvents: [
      {
        ...event,
        id: nextSharedFooterEventId++,
        createdAt: Date.now(),
      },
      ...state.footerEvents,
    ].slice(0, 6),
  })),
  pruneFooterEvents: () => set((state) => ({
    footerEvents: state.footerEvents.filter((event) => Date.now() - event.createdAt < 8_000),
  })),
  pruneRecentRemoteUpdates: () => set((state) => ({
    recentRemoteUpdates: Object.fromEntries(
      Object.entries(state.recentRemoteUpdates).filter(([, timestamp]) => Date.now() - timestamp < 8_000),
    ),
  })),
}));

export function upsertSharedPaneDraft(
  paneId: string,
  input: {
    serverId: string | null;
    queryId: string | null;
    dirty: boolean;
    snapshot: QueryDraftSnapshot | null;
  },
) {
  if (!input.serverId || !input.queryId || !input.snapshot) {
    useSharedSyncStore.getState().clearPaneDraft(paneId);
    return;
  }

  useSharedSyncStore.getState().setPaneDraft({
    paneId,
    serverId: input.serverId,
    queryId: input.queryId,
    dirty: input.dirty,
    touchedAt: Date.now(),
    snapshot: input.snapshot,
  });
}

export function clearSharedPaneDraft(paneId: string) {
  useSharedSyncStore.getState().clearPaneDraft(paneId);
}

function markQueriesAsSynced(querySnapshots: Record<string, QueryDraftSnapshot>) {
  if (Object.keys(querySnapshots).length === 0) return;
  useSharedSyncStore.getState().setQuerySyncedSnapshots(querySnapshots);
}

function markStoredQueriesAsSynced(queries: StoredQuery[]) {
  markQueriesAsSynced(
    Object.fromEntries(queries.map((query) => [query.id, toQueryDraftSnapshot(query)])),
  );
}

function markPayloadQueriesAsSynced(queries: SharedConfigPayloadQuery[]) {
  markQueriesAsSynced(
    Object.fromEntries(queries.map((query) => [query.id, payloadQueryToDraft(query)])),
  );
}

// ── Theme Store ─────────────────────────────────────────────────────

type ThemeMode = "system" | "light" | "dark";
type ResolvedTheme = "light" | "dark";

interface ThemeState {
  mode: ThemeMode;
  resolved: ResolvedTheme;
  setMode: (mode: ThemeMode) => void;
}

function resolveTheme(mode: ThemeMode): ResolvedTheme {
  if (mode === "system") {
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  }
  return mode;
}

function syncThemeColor() {
  const meta = document.querySelector('meta[name="theme-color"]');
  if (!meta) return;
  const color = getComputedStyle(document.documentElement).getPropertyValue("--color-surface").trim();
  if (color) {
    meta.setAttribute("content", color);
  }
}

function applyTheme(resolved: ResolvedTheme) {
  document.documentElement.classList.toggle("light", resolved === "light");
  syncThemeColor();
}

function loadThemeMode(): ThemeMode {
  const stored = localStorage.getItem("spuerhund:theme");
  if (stored === "light" || stored === "dark" || stored === "system") return stored;
  return "system";
}

export const useThemeStore = create<ThemeState>((set) => ({
  mode: loadThemeMode(),
  resolved: resolveTheme(loadThemeMode()),
  setMode: (mode) => {
    localStorage.setItem("spuerhund:theme", mode);
    const resolved = resolveTheme(mode);
    applyTheme(resolved);
    set({ mode, resolved });
  },
}));

export function initTheme() {
  const { mode } = useThemeStore.getState();
  const resolved = resolveTheme(mode);
  applyTheme(resolved);
  useThemeStore.setState({ resolved });

  // Listen for system preference changes
  const mq = window.matchMedia("(prefers-color-scheme: dark)");
  mq.addEventListener("change", () => {
    const { mode: currentMode } = useThemeStore.getState();
    if (currentMode === "system") {
      const newResolved = resolveTheme("system");
      applyTheme(newResolved);
      useThemeStore.setState({ resolved: newResolved });
    }
  });
}

// ── Initialize ───────────────────────────────────────────────────────

export async function refreshRuntimeStatus() {
  const current = useRuntimeStore.getState();
  const isDesktop = window.location.protocol === "file:";
  current.setSnapshot({
    reachable: current.reachable,
    paired: current.paired,
    version: current.version,
    agentProviders: current.agentProviders,
    checking: true,
  });

  try {
    const health = await getRuntimeHealth();
    useRuntimeStore.getState().setSnapshot({
      reachable: true,
      paired: health.paired,
      version: health.version,
      agentProviders: health.agentProviders,
      checking: false,
    });
    // Browser web app auth is localStorage-based. Only desktop mirrors runtime auth.
    if (isDesktop && health.authenticated && health.user) {
      useAuthStore.getState().setAuth({
        ...health.user,
        avatarUrl: health.user.avatarUrl ?? null,
      });
      // Fetch cloud credentials from runtime so authFetch can reach the Spürhund API
      try {
        const { getRuntimeBaseUrl } = await import("./runtimeClient");
        const { persistAuth } = await import("./auth");
        const credsResponse = await fetch(`${getRuntimeBaseUrl()}/v1/auth/credentials`);
        if (credsResponse.ok) {
          const creds = await credsResponse.json() as {
            accessToken: string;
            refreshToken: string;
            user: { id: string; email: string; displayName: string; avatarUrl?: string | null };
          };
          persistAuth({
            accessToken: creds.accessToken,
            refreshToken: creds.refreshToken,
            user: { ...creds.user, avatarUrl: creds.user.avatarUrl ?? null },
          });
        }
      } catch {
        // Credentials not available — sync will show error but app still works
      }
      // Switch to signed-in workspace if not already (desktop auth flow)
      if (useAppStore.getState().mode === "anonymous") {
        const { switchToSignedInDb } = await import("./dbContext");
        await switchToSignedInDb(health.user.id);
        useAppStore.getState().setMode("signed-in");
      }
    }
    scheduleRuntimeWorkspaceSync();
  } catch {
    useRuntimeStore.getState().setSnapshot({
      reachable: false,
      paired: false,
      version: "",
      agentProviders: [],
      checking: false,
    });
  }
}

export async function initialize(): Promise<{ startupWarnings: string[] }> {
  // If already authenticated (page reload), switch to user DB immediately
  const authState = useAuthStore.getState();
  if (authState.isAuthenticated && authState.user) {
    const { switchToSignedInDb } = await import("./dbContext");
    await switchToSignedInDb(authState.user.id);
  }

  useAppStore.getState().setMode(readStoredWorkspaceMode());
  useSecretsStore.getState().setSecrets(await db.listSecrets());
  const vaultSnapshot = await restoreRememberedVault();
  useVaultStore.getState().setSnapshot({
    hasVault: vaultSnapshot.hasVault,
    isUnlocked: vaultSnapshot.isUnlocked,
    isRemembered: vaultSnapshot.isRemembered,
    entries: vaultSnapshot.entries,
  });
  await refreshRuntimeStatus();
  const startupWarnings: string[] = [];
  let servers = await db.listServers();
  useServersStore.getState().setServers(servers);
  if (useAppStore.getState().mode === "signed-in" && !authState.isAuthenticated) {
    servers = await reconcileSignedInWorkspaceServers();
  }
  scheduleRuntimeWorkspaceSync();

  if (servers.length > 0) {
    await selectServer(servers[0]!.id);
  }

  return { startupWarnings };
}

async function refreshSecrets() {
  useSecretsStore.getState().setSecrets(await db.listSecrets());
}

function getSecretMap(): Record<string, string> {
  const secrets = useSecretsStore.getState().secrets;
  return Object.fromEntries(secrets.map((secret) => [secret.name, secret.value]));
}

export function isAdHocQuery(query: StoredQuery): boolean {
  return query.systemPreset === ADHOC_GQL_PRESET
    || query.systemPreset === ADHOC_REST_PRESET
    || query.systemPreset === ADHOC_MONGO_PRESET
    || query.systemPreset === ADHOC_SQL_PRESET;
}

function normalizeSavedCategoryName(category?: string | null): string | undefined {
  const trimmed = category?.trim();
  return trimmed ? trimmed : undefined;
}

function getSavedCategoryName(query: StoredQuery): string {
  return normalizeSavedCategoryName(query.category) ?? DEFAULT_SAVED_CATEGORY;
}

function findQueryByPreset(
  queries: StoredQuery[],
  preset: QuerySystemPreset,
): StoredQuery | undefined {
  return queries.find((query) => query.systemPreset === preset);
}

async function refreshQueriesForServer(serverId: string): Promise<StoredQuery[]> {
  const queries = await db.listQueries(serverId);
  useQueriesStore.getState().setQueries(queries);
  return queries;
}

async function ensureAdHocQueries(serverId: string): Promise<StoredQuery[]> {
  const server = useServersStore.getState().servers.find((entry) => entry.id === serverId);
  if (!server) return db.listQueries(serverId);
  let queries = await db.listQueries(serverId);
  const missingPresets = buildMissingAdHocPresets(server, queries);

  if (missingPresets.length === 0) {
    return queries;
  }

  for (const preset of missingPresets) await db.createQuery(preset);

  queries = await db.listQueries(serverId);
  return queries;
}

export async function selectAdHocQuery(
  serverId: string,
  preset: QuerySystemPreset,
) {
  const queries = await ensureAdHocQueries(serverId);
  useQueriesStore.getState().setQueries(queries);
  const query = findQueryByPreset(queries, preset);
  if (!query) {
    throw new Error(`Missing system query: ${preset}`);
  }
  await selectQuery(query);
}

async function markQueryLastUsed(queryId: string) {
  const lastUsedAt = new Date().toISOString();
  await db.updateQuery(queryId, { lastUsedAt });
  useQueriesStore.getState().setQueries(
    useQueriesStore.getState().queries.map((entry) =>
      entry.id === queryId ? { ...entry, lastUsedAt } : entry,
    ),
  );
}

export async function selectQueryInFocusedPane(query: StoredQuery) {
  const { activePaneId, assignQueryToPane } = useAppStore.getState();

  if (activePaneId === "primary") {
    await selectQuery(query);
    return;
  }

  assignQueryToPane(activePaneId, query.id);
  await markQueryLastUsed(query.id);
}

export async function selectAdHocQueryInFocusedPane(
  serverId: string,
  preset: QuerySystemPreset,
) {
  const queries = await ensureAdHocQueries(serverId);
  useQueriesStore.getState().setQueries(queries);
  const query = findQueryByPreset(queries, preset);
  if (!query) {
    throw new Error(`Missing system query: ${preset}`);
  }
  await selectQueryInFocusedPane(query);
}

// ── Server Actions ───────────────────────────────────────────────────

export async function addServer(
  name: string,
  baseUrl: string,
  authType = "none",
  authConfig?: string,
  isProduction?: boolean,
  paths?: {
    graphqlPath?: string;
    restPath?: string;
  },
  targetConfig?: {
    serverKind?: StoredServer["serverKind"];
    connectionConfig?: StoredServer["connectionConfig"];
  },
  timeoutMs?: number,
) {
  const server = await db.createServer({
    name,
    ...(targetConfig?.serverKind ? { serverKind: targetConfig.serverKind } : {}),
    ...(targetConfig?.connectionConfig ? { connectionConfig: targetConfig.connectionConfig } : {}),
    baseUrl,
    ...(paths?.graphqlPath ? { graphqlPath: paths.graphqlPath } : {}),
    ...(paths?.restPath ? { restPath: paths.restPath } : {}),
    authType,
    ...(authConfig !== undefined ? { authConfig } : {}),
    ...(isProduction !== undefined ? { isProduction } : {}),
    ...(timeoutMs != null ? { timeoutMs } : {}),
  });
  syncServerToD1(server);
  const servers = await db.listServers();
  useServersStore.getState().setServers(servers);
  await selectServer(server.id);
  if (useAppStore.getState().mode === "signed-in") {
    await ensureSignedInWorkspaceShare(server.id);
  }
  scheduleRuntimeWorkspaceSync();

  // Auto-introspect schema in background (don't block)
  if (normalizeServerKind(server.serverKind) === "http") {
    introspectSchema(server.id, { silent: true }).catch((error) => {
      console.warn("[introspect] auto-introspect failed:", error);
    });
  }

  return server;
}

export async function updateServer(id: string, updates: Partial<StoredServer>) {
  await db.updateServer(id, updates);
  authHeaderScriptCache.invalidate(id);
  const servers = await db.listServers();
  const updated = servers.find((s) => s.id === id);
  if (updated) syncServerToD1(updated);
  useServersStore.getState().setServers(servers);
  await scheduleSharedPushIfConnected(id);
  scheduleRuntimeWorkspaceSync();
}

export async function deleteServer(id: string) {
  await db.deleteServer(id);
  syncDeleteServerFromD1(id);
  authHeaderScriptCache.invalidate(id);
  const servers = await db.listServers();
  useServersStore.getState().setServers(servers);
  const vaultSnapshot = await refreshVaultSnapshot();
  useVaultStore.getState().setSnapshot({
    hasVault: vaultSnapshot.hasVault,
    isUnlocked: vaultSnapshot.isUnlocked,
    isRemembered: vaultSnapshot.isRemembered,
    entries: vaultSnapshot.entries,
  });
  const { activeServerId } = useServersStore.getState();
  if (activeServerId === id) {
    if (servers.length > 0) {
      await selectServer(servers[0]!.id);
    } else {
      useServersStore.getState().setActiveServerId(null);
      useQueriesStore.getState().setQueries([]);
    }
  }
  scheduleRuntimeWorkspaceSync();
}

export async function selectServer(id: string) {
  // Flush pending changes for the previous query before switching servers
  await flushAutoSave();

  // Close all secondary panes — queries belong to the previous server
  useAppStore.getState().closeAllSecondaryPanes();

  useServersStore.getState().setActiveServerId(id);
  useResultStore.getState().setConnectionStatus("unknown");
  const queries = await ensureAdHocQueries(id);
  useQueriesStore.getState().setQueries(queries);
  const server = useServersStore.getState().servers.find((entry) => entry.id === id);
  const defaultAdHocQuery = findQueryByPreset(queries, defaultAdHocPresetForServer(server));

  if (defaultAdHocQuery) {
    await selectQuery(defaultAdHocQuery);
  } else {
    useQueriesStore.getState().setActiveQueryId(null);
    useEditorStore.getState().setQueryText("");
    useEditorStore.getState().setVariables("{}");
    useEditorStore.getState().setHeaders("{}");
    useEditorStore.getState().setActiveTab("query");
    useEditorStore.getState().setRequestType("graphql");
    useEditorStore.getState().setRestMethod("GET");
    useEditorStore.getState().setRestPath("/");
    useEditorStore.getState().setRestParams([]);
    useEditorStore.getState().setRestContentType("json");
    useResultStore.getState().setResult(null);
    useResultStore.getState().setError(null);
    useQueryHistoryNavStore.getState().setQueryHistory([]);
    useQueryHistoryNavStore.getState().setHistoryCursor(null);
  }

  // Load cached schema
  const schema = await db.getSchemaCache(id);
  if (schema) {
    useSchemaStore.getState().setSchemaCache(id, schema);
  }

  // Load history
  const history = await db.listHistory(id, 50);
  useHistoryStore.getState().setEntries(history);
}

// ── Query Actions ────────────────────────────────────────────────────

export async function addQuery(
  serverId: string,
  name = "Saved Query",
  opts?: {
    id?: string;
    category?: string;
    systemPreset?: QuerySystemPreset;
    pinnedAt?: string | null;
    queryText?: string;
    variables?: string;
    headers?: string;
    remarks?: string;
    riskLevel?: db.QueryRiskLevel;
    requestType?: RequestType;
    operationConfig?: db.StoredQuery["operationConfig"];
    restMethod?: RestMethod;
    restPath?: string;
    restParams?: db.RestParam[];
    restContentType?: RestContentType;
  },
) {
  const input: Parameters<typeof db.createQuery>[0] = {
    serverId,
    name,
    ...(opts?.id ? { id: opts.id } : {}),
  };
  if (opts?.systemPreset === undefined && opts?.pinnedAt === undefined) {
    input.category = normalizeSavedCategoryName(opts?.category) ?? DEFAULT_SAVED_CATEGORY;
  } else if (opts?.category !== undefined) {
    input.category = normalizeSavedCategoryName(opts.category) ?? DEFAULT_SAVED_CATEGORY;
  }
  if (opts?.systemPreset !== undefined) input.systemPreset = opts.systemPreset;
  if (opts?.pinnedAt !== undefined) input.pinnedAt = opts.pinnedAt;
  if (opts?.queryText != null) input.queryText = opts.queryText;
  if (opts?.variables != null) input.variables = opts.variables;
  if (opts?.headers != null) input.headers = opts.headers;
  if (opts?.remarks != null) input.remarks = opts.remarks;
  if (opts?.riskLevel != null) input.riskLevel = opts.riskLevel;
  if (opts?.requestType != null) input.requestType = opts.requestType;
  if (opts?.operationConfig != null) input.operationConfig = opts.operationConfig;
  if (opts?.restMethod != null) input.restMethod = opts.restMethod;
  if (opts?.restPath != null) input.restPath = opts.restPath;
  if (opts?.restParams != null) input.restParams = opts.restParams;
  if (opts?.restContentType != null) input.restContentType = opts.restContentType;
  const query = await db.createQuery(input);
  if (!opts?.systemPreset) syncQueryToD1(query);
  const queries = await refreshQueriesForServer(serverId);
  const createdQuery = queries.find((entry) => entry.id === query.id) ?? query;
  await selectQuery(createdQuery);
  if (!opts?.systemPreset) {
    await scheduleSharedPushIfConnected(serverId);
  }
  return query;
}

export async function addRestQuery(serverId: string, name = "Saved REST Request") {
  return addQuery(serverId, name, {
    category: DEFAULT_SAVED_CATEGORY,
    requestType: "rest",
    restMethod: "GET",
    restPath: "/",
    restContentType: "json",
    queryText: "",
    variables: "{}",
  });
}

export async function updateQueryInDb(
  id: string,
  updates: Partial<StoredQuery>,
  options?: { syncShared?: boolean },
) {
  const existing = useQueriesStore.getState().queries.find((entry) => entry.id === id);
  const syncShared = options?.syncShared !== false;
  await db.updateQuery(id, updates);
  // Sync updated query to D1
  const allQueries = await db.listQueries(existing?.serverId ?? useServersStore.getState().activeServerId ?? "");
  const updatedQuery = allQueries.find((q) => q.id === id);
  if (updatedQuery) syncQueryToD1(updatedQuery);
  const { activeServerId } = useServersStore.getState();
  if (activeServerId) {
    await refreshQueriesForServer(activeServerId);
  }
  if (existing && !isAdHocQuery(existing) && syncShared) {
    await scheduleSharedPushIfConnected(existing.serverId);
  } else {
    scheduleRuntimeWorkspaceSync();
  }
}

export async function deleteQuery(id: string) {
  const query = useQueriesStore.getState().queries.find((entry) => entry.id === id);
  if (query && isAdHocQuery(query)) {
    return;
  }
  const wasActiveQuery = useQueriesStore.getState().activeQueryId === id;
  if (wasActiveQuery) {
    useQueriesStore.getState().setActiveQueryId(null);
    useQueryHistoryNavStore.getState().setHistoryCursor(null);
  }
  await db.deleteQuery(id);
  syncDeleteQueryFromD1(id);
  useAppStore.getState().closePanesForQuery(id);
  const { activeServerId } = useServersStore.getState();
  if (activeServerId) {
    await refreshQueriesForServer(activeServerId);
    if (wasActiveQuery) {
      await selectAdHocQuery(
        activeServerId,
        defaultAdHocPresetForServer(useServersStore.getState().servers.find((entry) => entry.id === activeServerId)),
      );
    }
  }
  if (query) {
    await scheduleSharedPushIfConnected(query.serverId);
  }
}

export async function toggleQueryPin(id: string) {
  const query = useQueriesStore.getState().queries.find((q) => q.id === id);
  if (!query || isAdHocQuery(query)) return;
  if (query.pinnedAt) {
    await db.updateQuery(id, { pinnedAt: null, category: getSavedCategoryName(query) });
  } else {
    await moveSavedQueryToPinned(id);
    return;
  }
  const { activeServerId } = useServersStore.getState();
  if (activeServerId) {
    await refreshQueriesForServer(activeServerId);
  }
  await scheduleSharedPushIfConnected(query.serverId);
}

export async function toggleQueriesPin(ids: string[]) {
  const queries = useQueriesStore.getState().queries.filter((query) => ids.includes(query.id) && !isAdHocQuery(query));
  if (queries.length === 0) return;

  const allPinned = queries.every((query) => query.pinnedAt);
  for (const query of queries) {
    if (allPinned) {
      await db.updateQuery(query.id, { pinnedAt: null, category: getSavedCategoryName(query) });
    } else {
      await moveSavedQueryToPinned(query.id);
    }
  }
  const { activeServerId } = useServersStore.getState();
  if (activeServerId) {
    await refreshQueriesForServer(activeServerId);
  }
  await scheduleSharedPushIfConnected(queries[0]!.serverId);
}

export async function deleteQueries(ids: string[]) {
  const protectedIds = new Set(
    useQueriesStore.getState().queries.filter(isAdHocQuery).map((query) => query.id),
  );
  const deletableIds = ids.filter((id) => !protectedIds.has(id));
  const { activeQueryId } = useQueriesStore.getState();
  const deletedActiveQuery = activeQueryId ? deletableIds.includes(activeQueryId) : false;

  if (deletedActiveQuery) {
    useQueriesStore.getState().setActiveQueryId(null);
    useQueryHistoryNavStore.getState().setHistoryCursor(null);
  }

  for (const id of deletableIds) {
    await db.deleteQuery(id);
    useAppStore.getState().closePanesForQuery(id);
  }
  const { activeServerId } = useServersStore.getState();
  if (activeServerId) {
    await refreshQueriesForServer(activeServerId);
  }
  if (activeServerId && deletedActiveQuery) {
    await selectAdHocQuery(
      activeServerId,
      defaultAdHocPresetForServer(useServersStore.getState().servers.find((entry) => entry.id === activeServerId)),
    );
  }
  if (deletableIds.length > 0 && activeServerId) {
    await scheduleSharedPushIfConnected(activeServerId);
  }
}

export async function reorderPinnedQueries(serverId: string, queryIds: string[]) {
  await db.reorderQueries(serverId, queryIds);
  await refreshQueriesForServer(serverId);
  await scheduleSharedPushIfConnected(serverId);
}

export async function moveSavedQueryToPinned(id: string, beforeId?: string) {
  const { activeServerId } = useServersStore.getState();
  if (!activeServerId) return;

  const serverQueries = useQueriesStore.getState().queries;
  const query = serverQueries.find((entry) => entry.id === id);
  if (!query || isAdHocQuery(query)) return;

  await db.updateQuery(id, {
    pinnedAt: query.pinnedAt ?? new Date().toISOString(),
    ...(query.category ? { category: query.category } : {}),
  });

  const refreshed = await refreshQueriesForServer(activeServerId);
  const pinnedIds = refreshed
    .filter((entry) => entry.pinnedAt && !isAdHocQuery(entry) && entry.id !== id)
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((entry) => entry.id);

  if (beforeId && pinnedIds.includes(beforeId)) {
    pinnedIds.splice(pinnedIds.indexOf(beforeId), 0, id);
  } else {
    pinnedIds.push(id);
  }

  await db.reorderQueries(activeServerId, pinnedIds);
  await refreshQueriesForServer(activeServerId);
  await scheduleSharedPushIfConnected(activeServerId);
}

export async function moveSavedQueryToCategory(id: string, category: string) {
  const { activeServerId } = useServersStore.getState();
  if (!activeServerId) return;

  const query = useQueriesStore.getState().queries.find((entry) => entry.id === id);
  if (!query || isAdHocQuery(query)) return;

  await db.updateQuery(id, {
    pinnedAt: null,
    category: normalizeSavedCategoryName(category) ?? DEFAULT_SAVED_CATEGORY,
  });

  await refreshQueriesForServer(activeServerId);
  await scheduleSharedPushIfConnected(activeServerId);
}

export async function saveQuerySnapshotAsSaved(input: {
  serverId: string;
  name: string;
  saveTo: "pinned" | "category";
  category?: string;
  snapshot: Pick<
    StoredQuery,
    "queryText" | "variables" | "headers" | "remarks" | "riskLevel" | "requestType" | "operationConfig" | "restMethod" | "restPath" | "restParams" | "restContentType"
  >;
}) {
  const trimmedName = input.name.trim();
  if (!trimmedName) throw new Error("Query name is required");

  const options: NonNullable<Parameters<typeof addQuery>[2]> = {
    queryText: input.snapshot.queryText,
    variables: input.snapshot.variables,
    headers: input.snapshot.headers,
    ...(input.snapshot.remarks !== undefined ? { remarks: input.snapshot.remarks } : {}),
    ...(input.snapshot.riskLevel !== undefined ? { riskLevel: input.snapshot.riskLevel } : {}),
  };
  if (input.snapshot.requestType !== undefined) options.requestType = input.snapshot.requestType;
  if (input.snapshot.operationConfig !== undefined) options.operationConfig = input.snapshot.operationConfig;
  if (input.snapshot.restMethod !== undefined) options.restMethod = input.snapshot.restMethod;
  if (input.snapshot.restPath !== undefined) options.restPath = input.snapshot.restPath;
  if (input.snapshot.restParams !== undefined) options.restParams = input.snapshot.restParams;
  if (input.snapshot.restContentType !== undefined) options.restContentType = input.snapshot.restContentType;
  if (input.saveTo === "category") {
    options.category = normalizeSavedCategoryName(input.category) ?? DEFAULT_SAVED_CATEGORY;
  } else {
    options.pinnedAt = new Date().toISOString();
  }

  const query = await addQuery(input.serverId, trimmedName, options);

  if (input.saveTo === "pinned") {
    await moveSavedQueryToPinned(query.id);
  }

  return query;
}

export async function saveCurrentQueryAsSaved(input: {
  name: string;
  saveTo: "pinned" | "category";
  category?: string;
  remarks?: string;
  riskLevel?: db.QueryRiskLevel;
}) {
  const { activeServerId } = useServersStore.getState();
  if (!activeServerId) throw new Error("No active server");

  const editor = useEditorStore.getState();
  const activeQueryId = useQueriesStore.getState().activeQueryId;
  const activeQuery = activeQueryId
    ? useQueriesStore.getState().queries.find((query) => query.id === activeQueryId)
    : null;
  return saveQuerySnapshotAsSaved({
    serverId: activeServerId,
    name: input.name,
    saveTo: input.saveTo,
    snapshot: {
      queryText: editor.queryText,
      variables: editor.variables,
      headers: editor.headers,
      remarks: input.remarks ?? activeQuery?.remarks ?? "",
      riskLevel: input.riskLevel ?? activeQuery?.riskLevel ?? DEFAULT_QUERY_RISK_LEVEL,
      requestType: editor.requestType,
      ...(activeQuery?.operationConfig ? { operationConfig: activeQuery.operationConfig } : {}),
      restMethod: editor.restMethod,
      restPath: editor.restPath,
      restParams: editor.restParams,
      restContentType: editor.restContentType,
    },
    ...(input.category !== undefined ? { category: input.category } : {}),
  });
}

export async function selectQuery(query: StoredQuery) {
  // Flush pending changes for the previous query before switching
  await flushAutoSave();

  useQueriesStore.getState().setActiveQueryId(query.id);
  useEditorStore.getState().setQueryText(query.queryText);
  useEditorStore.getState().setVariables(query.variables);
  useEditorStore.getState().setHeaders(query.headers);
  useEditorStore.getState().setRequestType(query.requestType ?? "graphql");
  useEditorStore.getState().setRestMethod(query.restMethod ?? "GET");
  useEditorStore.getState().setRestPath(query.restPath ?? "/");
  useEditorStore.getState().setRestParams(query.restParams ?? []);
  useEditorStore.getState().setRestContentType(query.restContentType ?? "json");

  // Track last-used timestamp
  await markQueryLastUsed(query.id);

  // Clear stale result
  useResultStore.getState().setResult(null);
  useResultStore.getState().setError(null);

  // Load per-query history
  const { activeServerId } = useServersStore.getState();
  if (activeServerId) {
    loadQueryHistory(activeServerId, query.id);
  }
}

// ── Auto-save current query ──────────────────────────────────────────

let saveTimeout: ReturnType<typeof setTimeout> | null = null;

export function scheduleAutoSave() {
  if (saveTimeout) clearTimeout(saveTimeout);
  saveTimeout = setTimeout(() => flushAutoSave(), 1000);
}

/** Immediately persist pending editor changes to DB + zustand store */
async function flushAutoSave() {
  if (saveTimeout) {
    clearTimeout(saveTimeout);
    saveTimeout = null;
  }
  const { activeQueryId } = useQueriesStore.getState();
  if (!activeQueryId) return;
  // Don't overwrite saved query with historical content
  const { historyCursor } = useQueryHistoryNavStore.getState();
  if (historyCursor !== null) return;
  const { queryText, variables, headers, requestType, restMethod, restPath, restParams, restContentType } = useEditorStore.getState();
  let savedQuery: db.StoredQuery;
  try {
    savedQuery = await db.updateQuery(activeQueryId, { queryText, variables, headers, requestType, restMethod, restPath, restParams, restContentType });
  } catch (error) {
    if (!isMissingQueryError(error)) {
      throw error;
    }

    const fallbackQuery = useQueriesStore.getState().queries.find((query) => query.id === activeQueryId);
    if (!fallbackQuery) {
      useQueriesStore.getState().setActiveQueryId(null);
    }
    return;
  }

  // Keep zustand queries store in sync with the normalized saved value so
  // switching back loads fresh data (the normalizer may have split query-string
  // params out of restPath into restParams).
  const queries = useQueriesStore.getState().queries.map((q) =>
    q.id === activeQueryId ? savedQuery : q,
  );
  useQueriesStore.getState().setQueries(queries);
  // Re-sync editor state in case the normalizer rewrote restPath/restParams
  // (e.g. user typed `?foo=bar` into the URL bar).
  if (savedQuery.restPath !== undefined && savedQuery.restPath !== restPath) {
    useEditorStore.getState().setRestPath(savedQuery.restPath);
  }
  const savedParams = savedQuery.restParams ?? [];
  if (JSON.stringify(savedParams) !== JSON.stringify(restParams)) {
    useEditorStore.getState().setRestParams(savedParams);
  }
  scheduleRuntimeWorkspaceSync();
}

// ── Auth header builder ──────────────────────────────────────────────

function normalizeStringRecord(input: unknown): Record<string, string> {
  if (!input || typeof input !== "object") return {};

  const record: Record<string, string> = {};
  for (const [key, value] of Object.entries(input)) {
    const trimmedKey = key.trim();
    if (!trimmedKey) continue;
    if (typeof value === "string") {
      record[trimmedKey] = value;
    } else if (value != null) {
      record[trimmedKey] = String(value);
    }
  }

  return record;
}

async function resolveRuntimeHeadersJson(
  rawHeaders: string,
  serverId: string,
): Promise<Record<string, string>> {
  if (!rawHeaders.trim()) return {};

  let parsed: unknown;
  try {
    parsed = JSON.parse(rawHeaders);
  } catch {
    return {};
  }

  const record = normalizeStringRecord(parsed);
  return resolveVaultTemplateRecord(record, browserVaultProvider, { serverId });
}

export async function buildAuthHeaders(server: StoredServer): Promise<Record<string, string>> {
  return buildStoredAuthHeaders({
    serverId: server.id,
    authType: server.authType,
    authConfig: server.authConfig,
    executeHeaderScript: (script) =>
      executeSharedHeaderScript(script, browserVaultProvider, { serverId: server.id }),
    headerScriptCache: authHeaderScriptCache,
    vaultProvider: browserVaultProvider,
    vaultContext: { serverId: server.id },
  });
}

// ── Shared GraphQL Request Execution ──────────────────────────────

export interface ExecuteGraphQLParams {
  server: StoredServer;
  queryText: string;
  variables: string;
  customHeaders: string;
  signal: AbortSignal;
}

export interface ExecuteGraphQLResult {
  data?: unknown;
  errors?: unknown[];
  statusCode: number;
  durationMs: number;
  responseText: string;
  corsError?: boolean;
  errorMessage?: string;
  debug: HttpDebugInfo;
}

/**
 * Core fetch logic for GraphQL requests — used by both primary and secondary panes.
 * Returns a structured result. Does NOT touch result/editor stores.
 */
export async function executeGraphQLRequest(params: ExecuteGraphQLParams): Promise<ExecuteGraphQLResult> {
  const { server, queryText, variables, customHeaders, signal } = params;
  let endpointUrl = "";

  const fetchHeaders: Record<string, string> = {
    "Content-Type": "application/json",
  };
  try {
    const resolvedUrls = await resolveServerUrls(
      server,
      browserVaultProvider,
      { serverId: server.id },
    );
    endpointUrl = resolvedUrls.graphqlEndpoint;
    const resolvedCustomHeaders = await resolveRuntimeHeadersJson(customHeaders, server.id);

    if (shouldUseRuntime()) {
      const runtimeResult = await executeRuntimeGraphql({
        serverId: server.id,
        endpointUrl,
        authType: server.authType,
        authConfig: server.authConfig,
        customHeaders: resolvedCustomHeaders,
        queryText,
        variables,
        ...(server.timeoutMs != null ? { timeoutMs: server.timeoutMs } : {}),
        vaultValues: await collectRuntimeVaultValues(server, {
          rawHeaders: customHeaders,
          rawVariables: variables,
          rawQueryText: queryText,
        }),
      }, signal);

      const debug = {
        request: {
          method: "POST",
          url: endpointUrl,
          headers: runtimeResult.requestHeaders,
          body: runtimeResult.requestBody,
        },
        response: {
          status: runtimeResult.statusCode,
          statusText: runtimeResult.redirected ? `Runtime via ${runtimeResult.finalUrl}` : "Runtime",
          headers: runtimeResult.responseHeaders,
          body: runtimeResult.responseText,
        },
        durationMs: runtimeResult.durationMs,
      } satisfies HttpDebugInfo;

      useHttpDebugStore.getState().setDebug(debug);

      return {
        ...(runtimeResult.data !== undefined ? { data: runtimeResult.data } : {}),
        ...(runtimeResult.errors ? { errors: runtimeResult.errors } : {}),
        statusCode: runtimeResult.statusCode,
        durationMs: runtimeResult.durationMs,
        responseText: runtimeResult.responseText,
        ...(runtimeResult.errorMessage ? { errorMessage: runtimeResult.errorMessage } : {}),
        debug,
      };
    }

    Object.assign(fetchHeaders, await buildAuthHeaders(server));
    Object.assign(fetchHeaders, resolvedCustomHeaders);
  } catch (error) {
    const message = handleRuntimeSetupError(error);
    const debug = {
      request: { method: "POST", url: endpointUrl, headers: fetchHeaders },
      error: message,
    };
    useHttpDebugStore.getState().setDebug(debug);
    return { statusCode: 0, durationMs: 0, responseText: "", errorMessage: message, debug };
  }

  let parsedVars: Record<string, unknown> | undefined;
  try {
    const v = JSON.parse(variables);
    if (typeof v === "object" && v !== null && Object.keys(v).length > 0) {
      parsedVars = v;
    }
  } catch { /* ignore */ }

  const requestBody = JSON.stringify({ query: queryText, variables: parsedVars });
  const start = performance.now();

  const effectiveSignal = withExecutionTimeoutSignal(signal, server.timeoutMs);

  try {
    const response = await fetch(endpointUrl, {
      method: "POST",
      headers: fetchHeaders,
      body: requestBody,
      signal: effectiveSignal,
    });

    const durationMs = Math.round(performance.now() - start);
    const responseText = await response.text();

    const debugBase = {
      request: { method: "POST", url: endpointUrl, headers: fetchHeaders, body: requestBody },
      response: { status: response.status, statusText: response.statusText, headers: responseHeaders(response), body: responseText },
      durationMs,
    };
    useHttpDebugStore.getState().setDebug(debugBase);

    let json: { data?: unknown; errors?: unknown[] };
    try {
      json = JSON.parse(responseText);
    } catch {
      return { statusCode: response.status, durationMs, responseText, errorMessage: `HTTP ${response.status}: non-JSON response`, debug: debugBase };
    }

    return {
      data: json.data,
      ...(json.errors ? { errors: json.errors } : {}),
      statusCode: response.status,
      durationMs,
      responseText,
      debug: debugBase,
    };
  } catch (error) {
    const durationMs = Math.round(performance.now() - start);
    const raw = error instanceof Error ? error.message : String(error);
    const isAbort = error instanceof DOMException && error.name === "AbortError";
    const isCors = !isAbort && error instanceof TypeError && /Failed to fetch|NetworkError|Load failed/i.test(raw);
    const message = isAbort
      ? "Request cancelled"
      : isCors
        ? `CORS error: ${raw}\n\nThe server at ${endpointUrl} likely doesn't include Access-Control-Allow-Origin headers for browser requests.`
        : raw;

    const debugErr = {
      request: { method: "POST", url: endpointUrl, headers: fetchHeaders, body: requestBody },
      error: message,
      durationMs,
    };
    useHttpDebugStore.getState().setDebug(debugErr);

    return { statusCode: 0, durationMs, responseText: "", errorMessage: message, corsError: isCors, debug: debugErr };
  }
}

// ── REST Request Execution ───────────────────────────────────────────

const REST_CONTENT_TYPE_MAP: Record<RestContentType, string> = {
  json: "application/json",
  xml: "application/xml",
  text: "text/plain",
  "form-urlencoded": "application/x-www-form-urlencoded",
};

const METHODS_WITH_BODY = new Set(["POST", "PUT", "PATCH"]);

export interface ExecuteRestParams {
  server: StoredServer;
  method: RestMethod;
  path: string;
  body: string;
  contentType: RestContentType;
  customHeaders: string;
  signal: AbortSignal;
}

export interface ExecuteRestResult {
  statusCode: number;
  durationMs: number;
  responseText: string;
  responseHeaders: Record<string, string>;
  responseContentType: string;
  errorMessage?: string;
  corsError?: boolean;
  debug: HttpDebugInfo;
}

export async function executeRestRequest(params: ExecuteRestParams): Promise<ExecuteRestResult> {
  const { server, method, path, body, contentType, customHeaders, signal } = params;
  let fullUrl = "";

  const fetchHeaders: Record<string, string> = {};
  try {
    const resolvedUrls = await resolveServerUrls(server, browserVaultProvider, { serverId: server.id });
    fullUrl = buildRestRequestUrl(
      { baseUrl: resolvedUrls.baseUrl, restPath: resolvedUrls.restPath },
      path,
    );
    const resolvedCustomHeaders = await resolveRuntimeHeadersJson(customHeaders, server.id);

    if (shouldUseRuntime()) {
      const runtimeHeaders = { ...resolvedCustomHeaders };
      if (METHODS_WITH_BODY.has(method)) {
        runtimeHeaders["Content-Type"] = REST_CONTENT_TYPE_MAP[contentType];
      }

      const runtimeResult = await executeRuntimeRest({
        serverId: server.id,
        url: fullUrl,
        method,
        authType: server.authType,
        authConfig: server.authConfig,
        headers: runtimeHeaders,
        ...(METHODS_WITH_BODY.has(method) && body.trim() ? { body } : {}),
        ...(server.timeoutMs != null ? { timeoutMs: server.timeoutMs } : {}),
        vaultValues: await collectRuntimeVaultValues(server, {
          rawHeaders: customHeaders,
          rawQueryText: body,
          rawRestPath: path,
        }),
      }, signal);

      const debugInfo: HttpDebugInfo = {
        request: {
          method,
          url: fullUrl,
          headers: runtimeResult.requestHeaders,
          ...(runtimeResult.requestBody !== undefined ? { body: runtimeResult.requestBody } : {}),
        },
        response: {
          status: runtimeResult.statusCode,
          statusText: runtimeResult.redirected ? `Runtime via ${runtimeResult.finalUrl}` : "Runtime",
          headers: runtimeResult.responseHeaders,
          body: runtimeResult.responseText,
        },
        durationMs: runtimeResult.durationMs,
      };
      useHttpDebugStore.getState().setDebug(debugInfo);

      return {
        statusCode: runtimeResult.statusCode,
        durationMs: runtimeResult.durationMs,
        responseText: runtimeResult.responseText,
        responseHeaders: runtimeResult.responseHeaders,
        responseContentType: runtimeResult.responseContentType,
        ...(runtimeResult.errorMessage ? { errorMessage: runtimeResult.errorMessage } : {}),
        debug: debugInfo,
      };
    }

    Object.assign(fetchHeaders, await buildAuthHeaders(server));

    if (METHODS_WITH_BODY.has(method)) {
      fetchHeaders["Content-Type"] = REST_CONTENT_TYPE_MAP[contentType];
    }

    Object.assign(fetchHeaders, resolvedCustomHeaders);
  } catch (error) {
    const message = handleRuntimeSetupError(error);
    const debug = {
      request: { method, url: fullUrl, headers: fetchHeaders },
      error: message,
    };
    useHttpDebugStore.getState().setDebug(debug);
    return {
      statusCode: 0,
      durationMs: 0,
      responseText: "",
      responseHeaders: {},
      responseContentType: "",
      errorMessage: message,
      debug,
    };
  }

  const restEffectiveSignal = withExecutionTimeoutSignal(signal, server.timeoutMs);

  const fetchOpts: RequestInit = {
    method,
    headers: fetchHeaders,
    signal: restEffectiveSignal,
  };

  if (METHODS_WITH_BODY.has(method) && body.trim()) {
    fetchOpts.body = body;
  }

  const requestBody = METHODS_WITH_BODY.has(method) ? body : "";
  const start = performance.now();

  try {
    const response = await fetch(fullUrl, fetchOpts);
    const durationMs = Math.round(performance.now() - start);
    const responseText = await response.text();
    const respHeaders = responseHeaders(response);
    const respContentType = response.headers.get("content-type") ?? "";

    const debugInfo: HttpDebugInfo = {
      request: { method, url: fullUrl, headers: fetchHeaders, body: requestBody },
      response: { status: response.status, statusText: response.statusText, headers: respHeaders, body: responseText },
      durationMs,
    };
    useHttpDebugStore.getState().setDebug(debugInfo);

    if (!response.ok) {
      return {
        statusCode: response.status,
        durationMs,
        responseText,
        responseHeaders: respHeaders,
        responseContentType: respContentType,
        errorMessage: `HTTP ${response.status} ${response.statusText}`,
        debug: debugInfo,
      };
    }

    return {
      statusCode: response.status,
      durationMs,
      responseText,
      responseHeaders: respHeaders,
      responseContentType: respContentType,
      debug: debugInfo,
    };
  } catch (error) {
    const durationMs = Math.round(performance.now() - start);
    const raw = error instanceof Error ? error.message : String(error);
    const isAbort = error instanceof DOMException && error.name === "AbortError";
    const isCors = !isAbort && error instanceof TypeError && /Failed to fetch|NetworkError|Load failed/i.test(raw);
    const message = isAbort
      ? "Request cancelled"
      : isCors
        ? `CORS error: ${raw}\n\nThe server at ${fullUrl} likely doesn't include Access-Control-Allow-Origin headers for browser requests.`
        : raw;

    const debugErr: HttpDebugInfo = {
      request: { method, url: fullUrl, headers: fetchHeaders, body: requestBody },
      error: message,
      durationMs,
    };
    useHttpDebugStore.getState().setDebug(debugErr);

    return { statusCode: 0, durationMs, responseText: "", responseHeaders: {}, responseContentType: "", errorMessage: message, corsError: isCors, debug: debugErr };
  }
}

// ── Query Execution (primary pane — uses singleton stores) ───────────

async function refreshHistoryAfterExecution(serverId: string, queryId: string | null) {
  const history = await db.listHistory(serverId, 50);
  useHistoryStore.getState().setEntries(history);
  useQueryHistoryNavStore.getState().setHistoryCursor(null);
  if (queryId) {
    await loadQueryHistory(serverId, queryId);
  }
}

export async function executeQuery() {
  const { activeServerId } = useServersStore.getState();
  const { queryText, variables, headers: customHeaders, requestType, restMethod, restPath, restParams, restContentType } = useEditorStore.getState();
  const { activeQueryId } = useQueriesStore.getState();
  const activeQuery = useQueriesStore.getState().queries.find((entry) => entry.id === activeQueryId);
  // REST execution URL combines the clean path with the separate params field.
  const restExecutionPath = buildRestPath(restPath, restParams);

  if (!activeServerId) return;
  // REST doesn't require body text, GQL does
  if ((requestType === "graphql" || requestType === "sql") && !queryText.trim()) return;

  await flushAutoSave();

  const server = useServersStore.getState().servers.find((s) => s.id === activeServerId);
  if (!server) return;

  if (activeQuery && !isAdHocQuery(activeQuery) && server.sharedConfigId) {
    const publishResult = await publishSharedServerIfConnected(activeServerId);
    if (publishResult === "conflict") {
      toast("Remote changes were detected. Resolve the conflict before executing.", "error");
      return;
    }
    if (publishResult === "failed") {
      return;
    }
  }

  const { controller: abortController, cleanup: cleanupAbortController } = createExecutionAbortController(server.timeoutMs);
  useResultStore.getState().setAbortController(abortController);
  useResultStore.getState().setExecutionStartedAt(performance.now());
  useResultStore.getState().setIsExecuting(true);
  useResultStore.getState().setError(null);
  if (activeQueryId) useExecutionTrackingStore.getState().markExecuting(activeQueryId);

  try {
    if (requestType === "rest") {
      // ── REST execution ──
      const result = await executeRestRequest({
        server,
        method: restMethod,
        path: restExecutionPath,
        body: queryText,
        contentType: restContentType,
        customHeaders,
        signal: abortController.signal,
      });

      if (result.errorMessage && result.statusCode === 0) {
        useResultStore.getState().setError(result.errorMessage);
        useResultStore.getState().setConnectionStatus("error");

        const errorEntry: Parameters<typeof db.addHistoryEntry>[0] = {
          serverId: activeServerId,
          queryText,
          variables,
          headers: customHeaders,
          durationMs: result.durationMs,
          errorMessage: result.errorMessage,
          executedAt: new Date().toISOString(),
          requestType: "rest",
          restMethod,
          restPath: restExecutionPath,
          restContentType,
        };
        if (activeQueryId) errorEntry.queryId = activeQueryId;
        await db.addHistoryEntry(errorEntry);
        await refreshHistoryAfterExecution(activeServerId, activeQueryId);
        return;
      }

      // Try to parse as JSON for structured display
      let parsedData: unknown = undefined;
      const isJson = result.responseContentType.includes("json");
      if (isJson) {
        try { parsedData = JSON.parse(result.responseText); } catch { /* raw text */ }
      }

      useResultStore.getState().setResult({
        requestType: "rest",
        ...(parsedData !== undefined ? { data: parsedData } : {}),
        statusCode: result.statusCode,
        durationMs: result.durationMs,
        responseHeaders: result.responseHeaders,
        responseBody: result.responseText,
        responseContentType: result.responseContentType,
      });
      useResultStore.getState().setConnectionStatus("connected");

      const historyEntry: Parameters<typeof db.addHistoryEntry>[0] = {
        serverId: activeServerId,
        queryText,
        variables,
        headers: customHeaders,
        responseBody: result.responseText,
        responseContentType: result.responseContentType,
        statusCode: result.statusCode,
        durationMs: result.durationMs,
        executedAt: new Date().toISOString(),
        requestType: "rest",
        restMethod,
        restPath: restExecutionPath,
        restContentType,
        ...(isJson ? { responseJson: result.responseText } : {}),
      };
      if (activeQueryId) historyEntry.queryId = activeQueryId;
      if (result.errorMessage) historyEntry.errorMessage = result.errorMessage;
      await db.addHistoryEntry(historyEntry);
      await refreshHistoryAfterExecution(activeServerId, activeQueryId);

    } else if (requestType === "graphql") {
      // ── GraphQL execution ──
      const result = await executeGraphQLRequest({
        server,
        queryText,
        variables,
        customHeaders,
        signal: abortController.signal,
      });

      if (result.errorMessage) {
        useResultStore.getState().setError(result.errorMessage);
        useResultStore.getState().setConnectionStatus("error");

        const errorEntry: Parameters<typeof db.addHistoryEntry>[0] = {
          serverId: activeServerId,
          queryText,
          variables,
          headers: customHeaders,
          durationMs: result.durationMs,
          errorMessage: result.errorMessage,
          executedAt: new Date().toISOString(),
          requestType: "graphql",
        };
        if (activeQueryId) errorEntry.queryId = activeQueryId;
        await db.addHistoryEntry(errorEntry);
        await refreshHistoryAfterExecution(activeServerId, activeQueryId);
        return;
      }

      useResultStore.getState().setResult({
        requestType: "graphql",
        data: result.data,
        ...(result.errors ? { errors: result.errors } : {}),
        statusCode: result.statusCode,
        durationMs: result.durationMs,
      });
      useResultStore.getState().setConnectionStatus("connected");

      const historyEntry: Parameters<typeof db.addHistoryEntry>[0] = {
        serverId: activeServerId,
        queryText,
        variables,
        headers: customHeaders,
        responseJson: result.responseText,
        statusCode: result.statusCode,
        durationMs: result.durationMs,
        executedAt: new Date().toISOString(),
        requestType: "graphql",
      };
      if (activeQueryId) historyEntry.queryId = activeQueryId;
      if (result.errors) historyEntry.errorMessage = JSON.stringify(result.errors);
      await db.addHistoryEntry(historyEntry);
      await refreshHistoryAfterExecution(activeServerId, activeQueryId);
    } else if (requestType === "mongo") {
      if (!activeQuery) {
        throw new Error("Mongo execution requires a saved query with operation config.");
      }
      const operationConfig = defaultQueryOperationConfig(activeQuery);
      const connection = defaultServerConnectionConfig(server);
      if (operationConfig?.kind !== "mongo" || connection.kind !== "mongo") {
        throw new Error("Mongo execution requires a Mongo server and Mongo query.");
      }
      // Transform MongoDB shell syntax (ObjectId, ISODate, etc.) to EJSON before execution
      const transformed = mongoShellToEjson(queryText);
      if (transformed.errors.length > 0) {
        console.warn("MongoDB shell transform warnings:", transformed.errors);
      }
      const transformedQueryText = transformed.ejson || queryText;

      const runtimeResult = await executeRuntimeMongo({
        serverId: server.id,
        connectionUri: connection.connectionUri,
        database: connection.database,
        collection: operationConfig.collection,
        operation: operationConfig.operation,
        queryText: transformedQueryText,
        variables,
        ...(server.timeoutMs != null ? { timeoutMs: server.timeoutMs } : {}),
        vaultValues: await collectRuntimeVaultValues(server, {
          rawHeaders: customHeaders,
          rawVariables: variables,
          rawQueryText: queryText,
          operationConfig,
        }),
      }, abortController.signal);

      useResultStore.getState().setResult({
        requestType: "mongo",
        ...(runtimeResult.data !== undefined ? { data: runtimeResult.data } : {}),
        statusCode: runtimeResult.statusCode,
        durationMs: runtimeResult.durationMs,
        responseBody: runtimeResult.responseText,
        responseContentType: "application/json",
      });
      useResultStore.getState().setConnectionStatus(runtimeResult.errorMessage ? "error" : "connected");
      if (runtimeResult.errorMessage) {
        useResultStore.getState().setError(runtimeResult.errorMessage);
      }

      await db.addHistoryEntry({
        serverId: activeServerId,
        queryText,
        variables,
        headers: customHeaders,
        responseBody: runtimeResult.responseText,
        responseContentType: "application/json",
        statusCode: runtimeResult.statusCode,
        durationMs: runtimeResult.durationMs,
        ...(runtimeResult.errorMessage ? { errorMessage: runtimeResult.errorMessage } : {}),
        executedAt: new Date().toISOString(),
        requestType: "mongo",
        operationConfig,
        ...(activeQueryId ? { queryId: activeQueryId } : {}),
      });
      await refreshHistoryAfterExecution(activeServerId, activeQueryId);
    } else if (requestType === "sql") {
      if (!activeQuery) {
        throw new Error("SQL execution requires a saved query.");
      }
      const operationConfig = defaultQueryOperationConfig(activeQuery);
      const connection = defaultServerConnectionConfig(server);
      if (connection.kind !== "postgres") {
        throw new Error("SQL execution requires a PostgreSQL server.");
      }
      const runtimeResult = await executeRuntimeSql({
        serverId: server.id,
        connectionUri: connection.connectionUri,
        statementText: queryText,
        paramsJson: variables,
        ...((operationConfig?.kind === "sql" ? operationConfig.defaultSchema : connection.defaultSchema)
          ? { defaultSchema: (operationConfig?.kind === "sql" ? operationConfig.defaultSchema : connection.defaultSchema)! }
          : {}),
        ...(server.timeoutMs != null ? { timeoutMs: server.timeoutMs } : {}),
        vaultValues: await collectRuntimeVaultValues(server, {
          rawHeaders: customHeaders,
          rawVariables: variables,
          rawQueryText: queryText,
          operationConfig: operationConfig?.kind === "sql" ? operationConfig : undefined,
        }),
      }, abortController.signal);

      useResultStore.getState().setResult({
        requestType: "sql",
        ...(runtimeResult.rows ? { data: runtimeResult.rows } : {}),
        statusCode: runtimeResult.statusCode,
        durationMs: runtimeResult.durationMs,
        responseBody: runtimeResult.responseText,
        responseContentType: "application/json",
        ...(runtimeResult.command ? { command: runtimeResult.command } : {}),
        ...(typeof runtimeResult.rowCount === "number" ? { rowCount: runtimeResult.rowCount } : {}),
      });
      useResultStore.getState().setConnectionStatus(runtimeResult.errorMessage ? "error" : "connected");
      if (runtimeResult.errorMessage) {
        useResultStore.getState().setError(runtimeResult.errorMessage);
      }

      await db.addHistoryEntry({
        serverId: activeServerId,
        queryText,
        variables,
        headers: customHeaders,
        responseBody: runtimeResult.responseText,
        responseContentType: "application/json",
        statusCode: runtimeResult.statusCode,
        durationMs: runtimeResult.durationMs,
        ...(runtimeResult.errorMessage ? { errorMessage: runtimeResult.errorMessage } : {}),
        executedAt: new Date().toISOString(),
        requestType: "sql",
        ...(operationConfig?.kind === "sql" ? { operationConfig } : {}),
        ...(activeQueryId ? { queryId: activeQueryId } : {}),
      });
      await refreshHistoryAfterExecution(activeServerId, activeQueryId);
    }
  } finally {
    cleanupAbortController();
    useResultStore.getState().setAbortController(null);
    useResultStore.getState().setExecutionStartedAt(null);
    useResultStore.getState().setIsExecuting(false);
    if (activeQueryId) useExecutionTrackingStore.getState().markDone(activeQueryId);
  }
}

// ── Schema Introspection (direct fetch from browser) ─────────────────

const INTROSPECTION_QUERY = `query IntrospectionQuery {
  __schema {
    queryType { name }
    mutationType { name }
    subscriptionType { name }
    types {
      kind name description
      fields(includeDeprecated: true) {
        name description
        args { name description type { ...TypeRef } defaultValue }
        type { ...TypeRef }
        isDeprecated deprecationReason
      }
      inputFields { name description type { ...TypeRef } defaultValue }
      interfaces { ...TypeRef }
      enumValues(includeDeprecated: true) { name description isDeprecated deprecationReason }
      possibleTypes { ...TypeRef }
    }
    directives { name description locations args { name description type { ...TypeRef } defaultValue } }
  }
}
fragment TypeRef on __Type {
  kind name ofType { kind name ofType { kind name ofType { kind name ofType { kind name ofType { kind name ofType { kind name } } } } } }
}`;

const inflightIntrospections = new Map<string, Promise<void>>();

export interface IntrospectSchemaOptions {
  silent?: boolean;
}

export function introspectSchema(serverId: string, options: IntrospectSchemaOptions = {}): Promise<void> {
  const existing = inflightIntrospections.get(serverId);
  if (existing) return existing;
  const promise = runIntrospectSchema(serverId, options).finally(() => {
    if (inflightIntrospections.get(serverId) === promise) {
      inflightIntrospections.delete(serverId);
    }
  });
  inflightIntrospections.set(serverId, promise);
  return promise;
}

async function runIntrospectSchema(serverId: string, options: IntrospectSchemaOptions): Promise<void> {
  const silent = options.silent === true;
  const errorToast = (msg: string) => {
    if (!silent) toast(`Introspection failed: ${msg}`, "error");
  };
  const successToast = (msg: string) => {
    if (!silent) toast(msg, "success");
  };
  const server = useServersStore.getState().servers.find(
    (s) => s.id === serverId,
  );
  if (!server) return;
  if (normalizeServerKind(server.serverKind) !== "http") {
    if (!shouldUseRuntime()) {
      if (!silent) toast("Database catalog refresh requires the Local Runtime.", "error");
      return;
    }

    try {
      await pushRuntimeWorkspaceSnapshotNow();
      const schema = await refreshRuntimeCatalog(serverId);
      const cached = await db.setSchemaCache(serverId, schema.introspectionJson, {
        ...(schema.catalogKind ? { catalogKind: schema.catalogKind } : {}),
        ...(schema.catalogJson ? { catalogJson: schema.catalogJson } : {}),
      });
      useSchemaStore.getState().setSchemaCache(serverId, cached);
      const catalog = schema.catalogJson ? JSON.parse(schema.catalogJson) as { entries?: unknown[] } : null;
      const entryCount = Array.isArray(catalog?.entries) ? catalog.entries.length : 0;
      successToast(`Catalog loaded${entryCount ? ` — ${entryCount} entries` : ""}`);
    } catch (error) {
      console.error("[introspect] catalog refresh error:", error);
      if (!silent) toast(formatIntrospectError(error) || "Catalog refresh failed", "error");
    }
    return;
  }
  let endpointUrl = "";
  let headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  try {
    const resolvedUrls = await resolveServerUrls(
      server,
      browserVaultProvider,
      { serverId: server.id },
    );
    endpointUrl = resolvedUrls.graphqlEndpoint;
    if (shouldUseRuntime()) {
      const result = await executeRuntimeGraphql({
        serverId: server.id,
        endpointUrl,
        authType: server.authType,
        authConfig: server.authConfig,
        queryText: INTROSPECTION_QUERY,
        vaultValues: await collectRuntimeVaultValues(server, {
          rawHeaders: "{}",
          rawQueryText: INTROSPECTION_QUERY,
        }),
      }, AbortSignal.timeout(60_000));

      useHttpDebugStore.getState().setDebug({
        request: {
          method: "POST",
          url: endpointUrl,
          headers: result.requestHeaders,
          body: result.requestBody,
        },
        response: {
          status: result.statusCode,
          statusText: result.redirected ? `Runtime via ${result.finalUrl}` : "Runtime",
          headers: result.responseHeaders,
          body: result.responseText,
        },
        durationMs: result.durationMs,
      });

      const rejectionReason =
        (result.rejection as { reason?: string } | undefined)?.reason ?? null;
      const httpFailure = result.statusCode >= 400 ? `Upstream HTTP ${result.statusCode}` : null;
      const failureMessage =
        (result.errorMessage && result.errorMessage.trim()) ||
        (rejectionReason && rejectionReason.trim()) ||
        httpFailure;

      if (failureMessage) {
        console.error("[introspect] runtime returned failure:", {
          serverId,
          statusCode: result.statusCode,
          errorMessage: result.errorMessage,
          rejection: result.rejection,
          responseText: result.responseText.slice(0, 500),
        });
        errorToast(failureMessage);
        return;
      }

      const types = (result.data as { __schema?: { types?: Array<{ name: string }> } } | undefined)?.__schema?.types;
      if (!types) {
        console.error("[introspect] runtime response missing __schema.types:", {
          serverId,
          statusCode: result.statusCode,
          responseText: result.responseText.slice(0, 500),
        });
        errorToast(
          `Empty schema (HTTP ${result.statusCode}) — ${result.responseText.slice(0, 200) || "no body"}`,
        );
        return;
      }

      const parsedJson = JSON.parse(result.responseText);
      const schema = await db.setSchemaCache(serverId, JSON.stringify(parsedJson));
      useSchemaStore.getState().setSchemaCache(serverId, schema);
      const typeCount = types.filter((t) => !t.name.startsWith("__")).length;
      successToast(`Schema loaded — ${typeCount} types`);
      return;
    }

    headers = {
      "Content-Type": "application/json",
      ...(await buildAuthHeaders(server)),
    };
  } catch (error) {
    console.error("[introspect] runtime path error:", error);
    const msg = handleRuntimeSetupError(error);
    useHttpDebugStore.getState().setDebug({
      request: { method: "POST", url: endpointUrl, headers },
      error: msg || formatIntrospectError(error),
    });
    if (msg === VAULT_LOCKED_RUNTIME_MESSAGE) {
      console.info("[introspect] aborted — vault locked");
    } else {
      errorToast(formatIntrospectError(error));
    }
    return;
  }

  const requestBody = JSON.stringify({ query: INTROSPECTION_QUERY });
  const start = performance.now();

  try {
    const response = await fetch(endpointUrl, {
      method: "POST",
      headers,
      body: requestBody,
      signal: AbortSignal.timeout(60_000),
    });

    const durationMs = Math.round(performance.now() - start);

    if (!response.ok) {
      const body = await response.text().catch(() => "");
      console.warn("[introspect] HTTP %d %s — %s", response.status, response.statusText, body.slice(0, 500));
      useHttpDebugStore.getState().setDebug({
        request: { method: "POST", url: endpointUrl, headers, body: requestBody },
        response: { status: response.status, statusText: response.statusText, headers: responseHeaders(response), body },
        durationMs,
      });
      errorToast(`HTTP ${response.status} — ${body.slice(0, 200) || "click debug for details"}`);
      return;
    }

    const json = await response.json();

    if (json.errors?.length && !json.data?.__schema) {
      const msg = json.errors.map((e: { message?: string }) => e.message).filter(Boolean).join("; ");
      console.warn("[introspect] GraphQL errors:", json.errors);
      useHttpDebugStore.getState().setDebug({
        request: { method: "POST", url: endpointUrl, headers, body: requestBody },
        response: { status: response.status, statusText: response.statusText, headers: responseHeaders(response), body: JSON.stringify(json) },
        durationMs,
      });
      errorToast(msg || `GraphQL errors with no message (${json.errors.length})`);
      return;
    }

    const types = json?.data?.__schema?.types;
    if (!types) {
      const snippet = JSON.stringify(json).slice(0, 500);
      console.warn("[introspect] No __schema.types in response:", snippet);
      errorToast(`Empty schema — ${snippet.slice(0, 200) || "no body"}`);
      return;
    }

    const schema = await db.setSchemaCache(serverId, JSON.stringify(json));
    useSchemaStore.getState().setSchemaCache(serverId, schema);
    const typeCount = types.filter((t: { name: string }) => !t.name.startsWith("__")).length;
    successToast(`Schema loaded — ${typeCount} types`);
  } catch (error) {
    const durationMs = Math.round(performance.now() - start);
    const msg = handleRuntimeSetupError(error);
    console.error("[introspect] HTTP path error:", error);
    useHttpDebugStore.getState().setDebug({
      request: { method: "POST", url: endpointUrl, headers, body: requestBody },
      error: msg || formatIntrospectError(error),
      durationMs,
    });
    if (msg === VAULT_LOCKED_RUNTIME_MESSAGE) {
      console.info("[introspect] aborted — vault locked");
    } else {
      errorToast(formatIntrospectError(error));
    }
  }
}

// ── Per-Query History Navigation ─────────────────────────────────────

export async function loadQueryHistory(serverId: string, queryId: string) {
  const entries = await db.listHistoryForQuery(serverId, queryId, 100);
  useQueryHistoryNavStore.getState().setQueryHistory(entries);
  useQueryHistoryNavStore.getState().setHistoryCursor(null);
}

export function navigateHistoryBack() {
  const { queryHistory, historyCursor } = useQueryHistoryNavStore.getState();
  if (queryHistory.length === 0) return;

  let newCursor: number;
  if (historyCursor === null) {
    // First back: go to most recent history entry
    newCursor = 0;
  } else if (historyCursor < queryHistory.length - 1) {
    newCursor = historyCursor + 1;
  } else {
    return; // already at oldest
  }

  useQueryHistoryNavStore.getState().setHistoryCursor(newCursor);
  applyHistoryEntry(queryHistory[newCursor]!);
}

export function navigateHistoryForward() {
  const { queryHistory, historyCursor } = useQueryHistoryNavStore.getState();
  if (historyCursor === null) return; // already live

  if (historyCursor <= 0) {
    // Back to live — restore from saved query
    useQueryHistoryNavStore.getState().setHistoryCursor(null);
    restoreLiveQuery();
    return;
  }

  const newCursor = historyCursor - 1;
  useQueryHistoryNavStore.getState().setHistoryCursor(newCursor);
  applyHistoryEntry(queryHistory[newCursor]!);
}

function applyHistoryEntry(entry: db.StoredHistoryEntry) {
  const editorState = restoreEditorStateFromHistory(entry);
  useEditorStore.getState().setQueryText(editorState.queryText);
  useEditorStore.getState().setVariables(editorState.variables);
  useEditorStore.getState().setHeaders(editorState.headers);
  useEditorStore.getState().setRequestType(editorState.requestType);
  useEditorStore.getState().setRestMethod(editorState.restMethod);
  useEditorStore.getState().setRestPath(editorState.restPath);
  useEditorStore.getState().setRestParams(editorState.restParams);
  useEditorStore.getState().setRestContentType(editorState.restContentType);

  const restored = restoreResultStateFromHistory(entry);
  if (restored.result) {
    useResultStore.getState().setResult(restored.result);
  } else {
    useResultStore.getState().setResult(null);
  }
  useResultStore.getState().setError(restored.error);
}

function restoreLiveQuery() {
  const { activeQueryId } = useQueriesStore.getState();
  if (!activeQueryId) return;
  const query = useQueriesStore.getState().queries.find((q) => q.id === activeQueryId);
  if (query) {
    const editorState = restoreEditorStateFromQuery(query);
    useEditorStore.getState().setQueryText(editorState.queryText);
    useEditorStore.getState().setVariables(editorState.variables);
    useEditorStore.getState().setHeaders(editorState.headers);
    useEditorStore.getState().setRequestType(editorState.requestType);
    useEditorStore.getState().setRestMethod(editorState.restMethod);
    useEditorStore.getState().setRestPath(editorState.restPath);
    useEditorStore.getState().setRestParams(editorState.restParams);
    useEditorStore.getState().setRestContentType(editorState.restContentType);
  }
  // Clear the result — back to live state
  useResultStore.getState().setResult(null);
  useResultStore.getState().setError(null);
}

// ── History Actions ──────────────────────────────────────────────────

export async function clearHistory(serverId: string) {
  await db.clearHistory(serverId);
  const history = await db.listHistory(serverId, 50);
  useHistoryStore.getState().setEntries(history);
}

// ── Secret Registry Actions ───────────────────────────────────────────

export async function saveSecret(name: string, value: string) {
  const trimmed = name.trim().toUpperCase();
  if (!trimmed) throw new Error("Secret name is required");
  if (!/^[A-Z0-9_]+$/.test(trimmed)) {
    throw new Error("Secret names may only contain A-Z, 0-9 and _");
  }
  await db.upsertSecret(trimmed, value);
  await refreshSecrets();
}

export async function removeSecret(name: string) {
  await db.deleteSecret(name);
  await refreshSecrets();
}

export async function exportSecrets(password: string): Promise<SecretExportBundle> {
  const data = await db.exportSecrets();
  const encrypted = await exportSecretsEncrypted(JSON.stringify(data), password);
  return {
    schemaVersion: 1,
    exportedAt: new Date().toISOString(),
    ...encrypted,
  };
}

export async function importSecretBundle(bundle: SecretExportBundle, password: string) {
  if (bundle.schemaVersion !== 1) throw new Error("Unsupported secret export version");
  const plaintext = await importSecretsEncrypted(bundle, password);
  await db.importSecrets(JSON.parse(plaintext) as db.SecretExportData);
  await refreshSecrets();
}

// ── Shared Config Actions ─────────────────────────────────────────────

export function collectRequiredVaultRefs(
  server: StoredServer,
  queries: StoredQuery[],
): SharedConfigRequiredVaultRef[] {
  const authConfig = parseAuthConfig(server.authConfig) as Record<string, unknown>;
  const refs = new Map<string, SharedConfigRequiredVaultRef>();

  const addRefs = (items: SharedConfigRequiredVaultRef[]) => {
    for (const item of items) {
      refs.set(`${item.kind}:${item.key}`, item);
    }
  };

  addRefs(collectStringVaultRefs(server.baseUrl));
  if (server.graphqlPath) addRefs(collectStringVaultRefs(server.graphqlPath));
  if (server.restPath) addRefs(collectStringVaultRefs(server.restPath));
  addRefs(collectJsonVaultRefs(server.connectionConfig));

  if (typeof authConfig.token === "string") addRefs(collectStringVaultRefs(authConfig.token));
  if (typeof authConfig.apiKey === "string") addRefs(collectStringVaultRefs(authConfig.apiKey));
  if (typeof authConfig.tokenScript === "string") addRefs(collectStringVaultRefs(authConfig.tokenScript));

  const staticHeaders =
    authConfig && typeof authConfig.headers === "object" && authConfig.headers !== null
      ? authConfig.headers
      : authConfig.customHeaders && typeof authConfig.customHeaders === "object"
        ? authConfig.customHeaders
        : null;

  if (staticHeaders) {
    for (const [headerName, headerValue] of Object.entries(staticHeaders)) {
      addRefs(collectStringVaultRefs(headerName));
      if (typeof headerValue === "string") addRefs(collectStringVaultRefs(headerValue));
    }
  }

  if (authConfig.mode === "script" && typeof authConfig.script === "string") {
    addRefs(collectStringVaultRefs(authConfig.script));
  }

  for (const query of queries) {
    addRefs(collectStringVaultRefs(query.headers));
    addRefs(collectStringVaultRefs(query.variables));
    if (query.restPath) addRefs(collectStringVaultRefs(query.restPath));
    addRefs(collectJsonVaultRefs(query.operationConfig));
  }

  return [...refs.values()]
    .filter((ref) => ref.kind === "secret")
    .sort((a, b) =>
    a.kind === b.kind
      ? a.key.localeCompare(b.key, undefined, { sensitivity: "base" })
      : a.kind.localeCompare(b.kind),
    );
}

export function validateServerVaultReferences(server: StoredServer, queries: StoredQuery[]) {
  const invalid = new Set<string>();
  const authConfig = parseAuthConfig(server.authConfig) as Record<string, unknown>;

  findInvalidVaultPlaceholders(server.baseUrl).forEach((value) => invalid.add(value));
  if (server.graphqlPath) findInvalidVaultPlaceholders(server.graphqlPath).forEach((value) => invalid.add(value));
  if (server.restPath) findInvalidVaultPlaceholders(server.restPath).forEach((value) => invalid.add(value));
  findInvalidVaultPlaceholders(JSON.stringify(server.connectionConfig ?? null)).forEach((value) => invalid.add(value));
  if (typeof authConfig.token === "string") findInvalidVaultPlaceholders(authConfig.token).forEach((value) => invalid.add(value));
  if (typeof authConfig.apiKey === "string") findInvalidVaultPlaceholders(authConfig.apiKey).forEach((value) => invalid.add(value));
  const staticHeaders =
    authConfig && typeof authConfig.headers === "object" && authConfig.headers !== null
      ? authConfig.headers
      : authConfig.customHeaders && typeof authConfig.customHeaders === "object"
        ? authConfig.customHeaders
        : null;
  if (staticHeaders) {
    for (const [headerName, headerValue] of Object.entries(staticHeaders)) {
      findInvalidVaultPlaceholders(headerName).forEach((entry) => invalid.add(entry));
      if (typeof headerValue === "string") {
        findInvalidVaultPlaceholders(headerValue).forEach((entry) => invalid.add(entry));
      }
    }
  }
  if (authConfig.mode === "script" && typeof authConfig.script === "string") {
    findInvalidVaultPlaceholders(authConfig.script).forEach((value) => invalid.add(value));
  }
  for (const query of queries) {
    findInvalidVaultPlaceholders(query.headers).forEach((value) => invalid.add(value));
    findInvalidVaultPlaceholders(query.variables).forEach((value) => invalid.add(value));
    if (query.restPath) {
      findInvalidVaultPlaceholders(query.restPath).forEach((value) => invalid.add(value));
    }
    findInvalidVaultPlaceholders(JSON.stringify(query.operationConfig ?? null)).forEach((value) => invalid.add(value));
  }
  return [...invalid];
}

async function buildSharedBundle(serverId: string): Promise<{
  server: StoredServer;
  queries: StoredQuery[];
  payload: ReturnType<typeof toSharedPayload>;
  payloadJson: string;
  fingerprint: string;
}> {
  const server = useServersStore.getState().servers.find((entry) => entry.id === serverId);
  if (!server) throw new Error("Server not found");

  const queries = overlayLocalPaneDrafts(
    serverId,
    (await db.listQueries(serverId)).filter((query) => !isAdHocQuery(query)),
  );
  const invalidRefs = validateServerVaultReferences(server, queries);
  if (invalidRefs.length > 0) {
    throw new Error(`Invalid vault placeholders: ${invalidRefs.join(", ")}`);
  }

  const payload = toSharedPayload(
    server,
    queries,
    collectRequiredVaultRefs(server, queries),
    await collectSharedConfigValues(serverId, server, queries),
  );
  const payloadJson = JSON.stringify(payload);

  return {
    server,
    queries,
    payload,
    payloadJson,
    fingerprint: payloadJson,
  };
}

function overlayLocalPaneDrafts(serverId: string, queries: StoredQuery[]): StoredQuery[] {
  const paneDrafts = Object.values(useSharedSyncStore.getState().paneDrafts)
    .filter((draft) => draft.serverId === serverId && draft.dirty);
  if (paneDrafts.length === 0) return queries;

  const latestDraftByQueryId = new Map<string, SharedPaneDraftState>();
  for (const draft of paneDrafts) {
    const existing = latestDraftByQueryId.get(draft.queryId);
    if (!existing || draft.touchedAt > existing.touchedAt) {
      latestDraftByQueryId.set(draft.queryId, draft);
    }
  }

  return queries.map((query) => {
    const draft = latestDraftByQueryId.get(query.id);
    if (!draft) return query;
    return {
      ...query,
      queryText: draft.snapshot.queryText,
      variables: draft.snapshot.variables,
      headers: draft.snapshot.headers,
      remarks: draft.snapshot.remarks ?? "",
      riskLevel: draft.snapshot.riskLevel ?? DEFAULT_QUERY_RISK_LEVEL,
      requestType: draft.snapshot.requestType ?? "graphql",
      ...(draft.snapshot.operationConfig !== undefined ? { operationConfig: draft.snapshot.operationConfig } : {}),
      restMethod: draft.snapshot.restMethod ?? "GET",
      restPath: draft.snapshot.restPath ?? "/",
      restParams: draft.snapshot.restParams ?? [],
      restContentType: draft.snapshot.restContentType ?? "json",
    };
  });
}

function parseSharedPayloadSnapshot(raw: string | undefined): SharedConfigPayload | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as SharedConfigPayload;
  } catch {
    return null;
  }
}

function actorLabel(input: {
  updatedByDisplayName?: string | null;
  updatedByClientId?: string | null;
  updatedBy?: { userId: string; displayName: string; avatarUrl: string | null } | null;
}): string {
  return input.updatedBy?.displayName || input.updatedByDisplayName || input.updatedByClientId || input.updatedBy?.userId || "someone";
}

function buildSecretDrafts(requiredRefs: SharedConfigRequiredVaultRef[]): PendingVaultRefDraft[] {
  return dedupePendingVaultDrafts(
    requiredRefs
      .filter((ref) => ref.kind === "secret")
      .map((ref) => ({
        kind: "secret" as const,
        key: ref.key,
        scopeType: "global" as const,
        description: "Created from shared server import",
        value: "",
      })),
  );
}

function buildSharedConfigDrafts(
  sharedConfigs: SharedConfigValue[],
  serverId: string,
): PendingVaultRefDraft[] {
  return dedupePendingVaultDrafts(
    sharedConfigs.map((entry) => ({
      kind: "config" as const,
      key: entry.key,
      scopeType: entry.scopeType,
      ...(entry.scopeType === "server" ? { scopeServerId: serverId } : {}),
      description: entry.description ?? "Synced from shared server",
      value: entry.value,
    })),
  );
}

async function materializeVaultDrafts(drafts: PendingVaultRefDraft[]) {
  const normalizedDrafts = dedupePendingVaultDrafts(drafts);
  if (normalizedDrafts.length === 0) return 0;

  const existing = useVaultStore.getState().entries;
  let applied = 0;

  for (const draft of normalizedDrafts) {
    const duplicate = existing.find((entry) =>
      entry.kind === draft.kind
      && entry.key === draft.key
      && entry.scopeType === draft.scopeType
      && (entry.scopeServerId ?? "") === (draft.scopeServerId ?? ""),
    );
    if (duplicate) {
      if (draft.kind !== "config") {
        continue;
      }

      await saveVaultEntry({
        id: duplicate.id,
        key: draft.key,
        kind: draft.kind,
        scopeType: draft.scopeType,
        ...(draft.scopeType === "server" && draft.scopeServerId ? { scopeServerId: draft.scopeServerId } : {}),
        description: draft.description ?? duplicate.description,
        value: draft.value ?? "",
      });
      applied += 1;
      continue;
    }

    await saveVaultEntry({
      key: draft.key,
      kind: draft.kind,
      scopeType: draft.scopeType,
      ...(draft.scopeType === "server" && draft.scopeServerId ? { scopeServerId: draft.scopeServerId } : {}),
      value: draft.value ?? "",
      description: draft.description ?? (draft.kind === "config" ? "Synced from shared server" : "Created from shared server import"),
    });
    applied += 1;
  }

  if (applied > 0) {
    const snapshot = await refreshVaultSnapshot();
    useVaultStore.getState().setSnapshot({
      hasVault: snapshot.hasVault,
      isUnlocked: snapshot.isUnlocked,
      isRemembered: snapshot.isRemembered,
      entries: snapshot.entries,
    });
  }

  return applied;
}

async function ensureSharedVaultRefs(input: {
  requiredRefs: SharedConfigRequiredVaultRef[];
  sharedConfigs: SharedConfigValue[];
  serverId: string;
}) {
  const drafts = dedupePendingVaultDrafts([
    ...buildSecretDrafts(input.requiredRefs),
    ...buildSharedConfigDrafts(input.sharedConfigs, input.serverId),
  ]);
  if (drafts.length === 0) return 0;

  const snapshot = await refreshVaultSnapshot();
  useVaultStore.getState().setSnapshot({
    hasVault: snapshot.hasVault,
    isUnlocked: snapshot.isUnlocked,
    isRemembered: snapshot.isRemembered,
    entries: snapshot.entries,
  });

  const missingDrafts = findMissingVaultRefDrafts(drafts, snapshot.entries, input.serverId);
  if (missingDrafts.length === 0) {
    useAppStore.getState().clearPendingVaultRefs();
    return 0;
  }

  if (snapshot.hasVault && snapshot.isUnlocked) {
    const applied = await materializeVaultDrafts(missingDrafts);
    if (applied > 0) {
      toast(
        applied === 1
          ? "1 local vault entry was prepared from the shared server."
          : `${applied} local vault entries were prepared from the shared server.`,
        "info",
      );
      useAppStore.getState().setShowVaultManager(true);
    }
    return applied;
  }

  useAppStore.getState().setPendingVaultRefs(missingDrafts);
  useAppStore.getState().setShowVaultManager(true);
  toast(
    snapshot.hasVault
      ? "Shared server needs local vault entries. Unlock the vault to prepare them."
      : "Shared server needs a local vault. Create it first to prepare secrets and shared config values.",
    "info",
  );
  return 0;
}

function buildRemoteSummary(input: {
  changed: number;
  deleted: number;
  conflicts: number;
}): { tone: SharedFooterEvent["tone"]; message: string } | null {
  if (input.changed === 0 && input.deleted === 0 && input.conflicts === 0) {
    return null;
  }

  const parts: string[] = [];
  if (input.changed > 0) {
    parts.push(input.changed === 1 ? "1 query updated" : `${input.changed} queries updated`);
  }
  if (input.deleted > 0) {
    parts.push(input.deleted === 1 ? "1 query deleted" : `${input.deleted} queries deleted`);
  }
  if (input.conflicts > 0) {
    parts.push(input.conflicts === 1 ? "1 conflict waiting" : `${input.conflicts} conflicts waiting`);
  }

  return {
    tone: input.conflicts > 0 || input.deleted > 0 ? "error" : "success",
    message: parts.join(", "),
  };
}

async function applySharedConfigDocument(
  serverId: string,
  remote: SharedConfigDocument,
  remotePayload: SharedConfigPayload,
  remotePayloadJson: string,
  input: {
    serverPayload: SharedConfigPayload["server"];
    queryPayloads: SharedConfigPayloadQuery[];
    remoteChanged: boolean;
    hasServerConflict: boolean;
    changedQueryIds: string[];
    deletedQueryIds: string[];
  },
) {
  const server = useServersStore.getState().servers.find((entry) => entry.id === serverId);
  const ref = await db.getSharedConfigRefByServerId(serverId);
  if (!server || !ref) throw new Error("Shared config not connected");

  await ensureSharedVaultRefs({
    requiredRefs: getSharedPayloadRequiredVaultRefs(remotePayload),
    sharedConfigs: getSharedPayloadSharedConfigs(remotePayload),
    serverId,
  });

  const nextServer: StoredServer = {
    ...server,
    ...resolveSharedPayloadServer(input.serverPayload),
    sharedLastKnownVersion: remote.version,
    updatedAt: new Date().toISOString(),
  };
  const nextRef: db.StoredSharedConfigRef = {
    ...ref,
    lastKnownVersion: remote.version,
    lastSyncedFingerprint: remotePayloadJson,
    lastSyncedAt: new Date().toISOString(),
    lastRemoteUpdateAt: remote.updatedAt,
    lastRemoteUpdatedByClientId: remote.updatedBy?.userId ?? "unknown",
    ...(remote.updatedBy?.displayName ? { lastRemoteUpdatedByDisplayName: remote.updatedBy.displayName } : {}),
    remoteChanged: input.remoteChanged,
    updatedAt: new Date().toISOString(),
  };

  await db.replaceServerBundle({
    mode: "update",
    server: nextServer,
    queries: materializeSharedQueries(serverId, input.queryPayloads),
    sharedRef: nextRef,
  });

  useServersStore.getState().setServers(await db.listServers());
  markPayloadQueriesAsSynced(remotePayload.queries);
  if (input.deletedQueryIds.length > 0) {
    useSharedSyncStore.getState().clearQuerySyncedSnapshots(input.deletedQueryIds);
  }
  useSharedSyncStore.getState().setServerRemoteInfo(serverId, {
    updatedAt: remote.updatedAt,
    actor: actorLabel(remote),
    queryIds: input.changedQueryIds,
  });
  useSharedSyncStore.getState().setServerConflict(serverId, input.hasServerConflict);
  if (input.changedQueryIds.length > 0) {
    useSharedSyncStore.getState().markRecentRemoteUpdates(input.changedQueryIds);
  }

  if (input.deletedQueryIds.length > 0) {
    for (const queryId of input.deletedQueryIds) {
      useAppStore.getState().closePanesForQuery(queryId);
    }
  }

  const { activeServerId } = useServersStore.getState();
  if (activeServerId === serverId) {
    const previousActiveQueryId = useQueriesStore.getState().activeQueryId;
    const queries = await refreshQueriesForServer(serverId);
    if (previousActiveQueryId && !queries.some((query) => query.id === previousActiveQueryId)) {
      await selectAdHocQuery(
        serverId,
        defaultAdHocPresetForServer(useServersStore.getState().servers.find((entry) => entry.id === serverId)),
      );
    }
  }
  scheduleRuntimeWorkspaceSync();
}

async function fetchSharedConfigPayload(
  serverId: string,
): Promise<{
  server: StoredServer;
  ref: db.StoredSharedConfigRef;
  remote: SharedConfigDocument;
  payload: SharedConfigPayload;
  payloadJson: string;
} | null> {
  const server = useServersStore.getState().servers.find((entry) => entry.id === serverId);
  const ref = await db.getSharedConfigRefByServerId(serverId);
  if (!server || !ref) return null;

  const remote = await getSharedConfig(ref.configId);
  const payloadJson = remote.payload;
  const payload = JSON.parse(payloadJson) as SharedConfigPayload;
  return { server, ref, remote, payload, payloadJson };
}

export async function processSharedConfigRemoteUpdate(
  serverId: string,
  input: {
    updatedAt: string;
    updatedByClientId: string;
    updatedByDisplayName?: string | null;
  },
) {
  const remoteState = await fetchSharedConfigPayload(serverId);
  if (!remoteState) return;

  const basePayload = parseSharedPayloadSnapshot(remoteState.ref.lastSyncedFingerprint);
  const localQueries = (await db.listQueries(serverId)).filter((query) => !isAdHocQuery(query));
  const localQueryById = new Map(localQueries.map((query) => [query.id, query]));
  const baseQueryById = new Map((basePayload?.queries ?? []).map((query) => [query.id, query]));
  const remoteQueryById = new Map(remoteState.payload.queries.map((query) => [query.id, query]));
  const dirtyQueryIds = new Set(
    Object.values(useSharedSyncStore.getState().paneDrafts)
      .filter((draft) => draft.serverId === serverId && draft.dirty)
      .map((draft) => draft.queryId),
  );

  const changedQueryIds: string[] = [];
  const deletedQueryIds: string[] = [];
  const conflictQueryIds: string[] = [];
  const conflicts: SharedQueryConflictState[] = [];
  const nextQueryPayloads: SharedConfigPayloadQuery[] = [];

  const allQueryIds = [...new Set([
    ...localQueryById.keys(),
    ...baseQueryById.keys(),
    ...remoteQueryById.keys(),
  ])].sort((a, b) => a.localeCompare(b));

  for (const queryId of allQueryIds) {
    const localQuery = localQueryById.get(queryId);
    const localPayloadQuery = localQuery ? storedQueryToPayloadQuery(localQuery) : undefined;
    const baseQuery = baseQueryById.get(queryId);
    const remoteQuery = remoteQueryById.get(queryId);
    const remoteChanged = !payloadQueriesEqual(baseQuery, remoteQuery);

    if (!remoteChanged) {
      if (localPayloadQuery) {
        nextQueryPayloads.push(localPayloadQuery);
      }
      continue;
    }

    if (dirtyQueryIds.has(queryId) && !payloadQueriesEqual(localPayloadQuery, remoteQuery)) {
      conflictQueryIds.push(queryId);
      conflicts.push({
        queryId,
        serverId,
        queryName: localQuery?.name ?? remoteQuery?.name ?? "Saved Query",
        updatedAt: input.updatedAt,
        updatedByClientId: input.updatedByClientId,
        updatedByDisplayName: input.updatedByDisplayName ?? null,
        remoteDeleted: !remoteQuery,
      });
      if (localPayloadQuery) {
        nextQueryPayloads.push(localPayloadQuery);
      }
      continue;
    }

    if (remoteQuery) {
      nextQueryPayloads.push(remoteQuery);
      changedQueryIds.push(queryId);
    } else {
      deletedQueryIds.push(queryId);
    }
  }

  const serverConflict = Boolean(
    basePayload && !payloadServersEqual(basePayload.server, remoteState.payload.server),
  );

  await applySharedConfigDocument(
    serverId,
    remoteState.remote,
    remoteState.payload,
    remoteState.payloadJson,
    {
      serverPayload: remoteState.payload.server,
      queryPayloads: nextQueryPayloads,
      remoteChanged: conflicts.length > 0 || serverConflict,
      hasServerConflict: serverConflict,
      changedQueryIds,
      deletedQueryIds,
    },
  );

  useSharedSyncStore.getState().clearServerConflicts(serverId, conflictQueryIds);
  for (const conflict of conflicts) {
    useSharedSyncStore.getState().upsertQueryConflict(conflict);
  }

  const summary = buildRemoteSummary({
    changed: changedQueryIds.length,
    deleted: deletedQueryIds.length,
    conflicts: conflicts.length + (serverConflict ? 1 : 0),
  });
  if (summary) {
    toast(
      `${summary.message} by ${actorLabel(input)}`,
      summary.tone === "error" ? "error" : summary.tone === "success" ? "success" : "info",
    );
  }
}

async function autoPushSharedServer(serverId: string): Promise<"pushed" | "conflict" | "failed" | "noop"> {
  const ref = await db.getSharedConfigRefByServerId(serverId);
  if (!ref) return "noop";

  useSharedSyncStore.getState().setServerPushing(serverId, true);
  try {
    await createOrUpdateSharedConfig(serverId);
    useSharedSyncStore.getState().clearServerConflicts(serverId);
    useSharedSyncStore.getState().setServerConflict(serverId, false);
    return "pushed";
  } catch (error) {
    const message = errorMessage(error);
    if (message.includes("Conflict")) {
      await processSharedConfigRemoteUpdate(serverId, {
        updatedAt: new Date().toISOString(),
        updatedByClientId: "remote",
        updatedByDisplayName: "Remote change",
      });
      return "conflict";
    }
    toast(message, "error");
    return "failed";
  } finally {
    useSharedSyncStore.getState().setServerPushing(serverId, false);
  }
}

function scheduleSharedServerPush(serverId: string) {
  const timer = autoPushTimers.get(serverId);
  if (timer) {
    window.clearTimeout(timer);
  }

  const nextTimer = window.setTimeout(() => {
    autoPushTimers.delete(serverId);
    void autoPushSharedServer(serverId);
  }, AUTO_PUSH_DEBOUNCE_MS);

  autoPushTimers.set(serverId, nextTimer);
}

async function scheduleSharedPushIfConnected(serverId: string) {
  const ref = await db.getSharedConfigRefByServerId(serverId);
  if (!ref) {
    scheduleRuntimeWorkspaceSync();
    return;
  }
  scheduleSharedServerPush(serverId);
  scheduleRuntimeWorkspaceSync();
}

export async function publishSharedServerIfConnected(serverId: string): Promise<"pushed" | "conflict" | "failed" | "noop"> {
  const result = await autoPushSharedServer(serverId);
  scheduleRuntimeWorkspaceSync();
  return result;
}

export async function getSharedSyncSnapshot(serverId: string): Promise<{
  server: StoredServer;
  ref: db.StoredSharedConfigRef;
  fingerprint: string;
} | null> {
  const server = useServersStore.getState().servers.find((entry) => entry.id === serverId);
  const ref = await db.getSharedConfigRefByServerId(serverId);
  if (!server || !ref) return null;
  try {
    const { fingerprint } = await buildSharedBundle(serverId);
    return { server, ref, fingerprint };
  } catch (error) {
    if (error instanceof MissingVaultConfigError) return null;
    throw error;
  }
}

export async function getSharedMembersForServer(serverId: string): Promise<SharedConfigMember[]> {
  const ref = await db.getSharedConfigRefByServerId(serverId);
  if (!ref) return [];
  const response = await listSharedConfigMembers(ref.configId);
  // Map new API member shape to legacy SharedConfigMember for compatibility
  return response.members.map((m) => ({
    clientId: m.userId,
    displayName: m.displayName,
    role: m.role,
    joinedAt: m.joinedAt,
    lastSeenAt: m.lastSeenAt,
  }));
}

export async function getSharedStreamConnectionInput(serverId: string): Promise<{
  configId: string;
  sinceVersion: number;
} | null> {
  const ref = await db.getSharedConfigRefByServerId(serverId);
  if (!ref) return null;
  return {
    configId: ref.configId,
    sinceVersion: ref.lastKnownVersion,
  };
}

export async function materializePendingVaultRefsInApp() {
  const drafts = useAppStore.getState().pendingVaultRefs;
  if (drafts.length === 0) return 0;
  const pending = findMissingVaultRefDrafts(
    dedupePendingVaultDrafts(drafts),
    useVaultStore.getState().entries,
  );
  if (pending.length === 0) {
    useAppStore.getState().clearPendingVaultRefs();
    return 0;
  }
  const created = await materializeVaultDrafts(pending);
  useAppStore.getState().clearPendingVaultRefs();
  return created;
}

export async function checkSharedConfigUpdates(serverId: string): Promise<{
  action: "none" | "pulled" | "remoteChanged";
  updatedByClientId?: string;
  updatedByDisplayName?: string | null;
} | null> {
  // With JWT auth, polling for changes uses getSharedConfig directly
  const snapshot = await getSharedSyncSnapshot(serverId);
  if (!snapshot) return null;

  const remote = await getSharedConfig(snapshot.ref.configId);
  if (remote.version <= snapshot.ref.lastKnownVersion) {
    return { action: "none" };
  }

  const updatedByUserId = remote.updatedBy?.userId ?? "unknown";
  const updatedByDisplayName = remote.updatedBy?.displayName ?? null;

  await processSharedConfigRemoteUpdate(serverId, {
    updatedAt: remote.updatedAt,
    updatedByClientId: updatedByUserId,
    updatedByDisplayName,
  });
  const ref = await db.getSharedConfigRefByServerId(serverId);
  return ref?.remoteChanged
    ? {
        action: "remoteChanged",
        updatedByClientId: updatedByUserId,
        updatedByDisplayName,
      }
    : {
        action: "pulled",
        updatedByClientId: updatedByUserId,
        updatedByDisplayName,
      };
}

export function markSharedConfigRemoteChanged(
  serverId: string,
  input: {
    updatedAt: string;
    updatedByClientId: string;
    updatedByDisplayName?: string | null;
  },
) {
  db.getSharedConfigRefByServerId(serverId).then((ref) => {
    if (!ref) return;
    return db.putSharedConfigRef({
      ...ref,
      lastRemoteUpdateAt: input.updatedAt,
      lastRemoteUpdatedByClientId: input.updatedByClientId,
      ...(input.updatedByDisplayName ? { lastRemoteUpdatedByDisplayName: input.updatedByDisplayName } : {}),
      remoteChanged: true,
    });
  }).catch(() => {});
  useSharedSyncStore.getState().setServerRemoteInfo(serverId, {
    updatedAt: input.updatedAt,
    actor: actorLabel(input),
  });
  useSharedSyncStore.getState().setServerConflict(serverId, true);
}

async function upsertSharedServerState(
  serverId: string,
  configId: string,
  role: SharedConfigRole,
  version: number,
  displayName: string,
  fingerprint: string,
) {
  const existingRef = await db.getSharedConfigRef(configId);
  await db.setServerSharedState(serverId, {
    sharedConfigId: configId,
    sharedRole: role,
    sharedLastKnownVersion: version,
    sharedCapability: configId, // legacy field, kept for compat
  });
  await db.putSharedConfigRef({
    ...(existingRef ?? {}),
    configId,
    linkedServerId: serverId,
    role,
    sharedKey: "", // no longer used — payloads are plaintext
    clientId: "", // no longer used — JWT auth identifies the user
    displayName,
    lastKnownVersion: version,
    lastSyncedFingerprint: fingerprint,
    lastSyncedAt: new Date().toISOString(),
    remoteChanged: false,
  });
  useServersStore.getState().setServers(await db.listServers());
  useSharedSyncStore.getState().setServerConflict(serverId, false);
}

export async function createOrUpdateSharedConfig(serverId: string): Promise<string> {
  const existingRef = await db.getSharedConfigRefByServerId(serverId);
  const displayName = existingRef?.displayName || ensureSharedDisplayName();
  if (!readSharedDisplayName()) {
    setSharedDisplayName(displayName);
  }
  const { queries, payloadJson, fingerprint } = await buildSharedBundle(serverId);
  if (!existingRef) {
    const created = await createSharedConfig(payloadJson);
    await upsertSharedServerState(
      serverId,
      created.configId,
      "owner",
      created.version,
      displayName,
      fingerprint,
    );
    markStoredQueriesAsSynced(queries);
    scheduleRuntimeWorkspaceSync();
    return created.configId;
  }

  const updated = await updateSharedConfig(
    existingRef.configId,
    payloadJson,
    existingRef.lastKnownVersion,
  );
  await upsertSharedServerState(
    serverId,
    existingRef.configId,
    existingRef.role,
    updated.version,
    existingRef.displayName || displayName,
    fingerprint,
  );
  markStoredQueriesAsSynced(queries);
  scheduleRuntimeWorkspaceSync();
  return existingRef.configId;
}

async function ensureSignedInWorkspaceShare(serverId: string) {
  const authState = useAuthStore.getState();
  if (useAppStore.getState().mode !== "signed-in" || !authState.isAuthenticated || !authState.user) {
    return;
  }
  try {
    await createOrUpdateSharedConfig(serverId);
  } catch (error) {
    // Best-effort during init — never crash the app for sync failures
    console.warn("[sync] ensureSignedInWorkspaceShare failed for", serverId, error);
  }
}

async function reconcileSignedInWorkspaceServers(): Promise<StoredServer[]> {
  if (useAppStore.getState().mode !== "signed-in") {
    return db.listServers();
  }

  let servers = await db.listServers();
  let changed = false;

  for (const server of servers) {
    if (server.sharedConfigId) continue;
    await ensureSignedInWorkspaceShare(server.id);
    changed = true;
  }

  servers = await db.listServers();
  if (changed) {
    useServersStore.getState().setServers(servers);
  }

  scheduleRuntimeWorkspaceSync();
  return servers;
}

export async function reconcileSignedInWorkspaceAfterAuth(): Promise<void> {
  const servers = await reconcileSignedInWorkspaceServers();
  useServersStore.getState().setServers(servers);
}

export async function enableSignedInWorkspace() {
  useAppStore.getState().setMode("signed-in");
  // useUserDataSync handles D1 pull + account vault init when the signed-in workspace becomes active.
  scheduleRuntimeWorkspaceSync();
}

export async function disableSignedInWorkspace() {
  useAppStore.getState().setMode("anonymous");
  scheduleRuntimeWorkspaceSync();
}

export async function pullSharedConfig(serverId: string) {
  const remoteState = await fetchSharedConfigPayload(serverId);
  if (!remoteState) throw new Error("Shared config not connected");
  await applySharedConfigDocument(
    serverId,
    remoteState.remote,
    remoteState.payload,
    remoteState.payloadJson,
    {
      serverPayload: remoteState.payload.server,
      queryPayloads: remoteState.payload.queries,
      remoteChanged: false,
      hasServerConflict: false,
      changedQueryIds: remoteState.payload.queries.map((query) => query.id),
      deletedQueryIds: [],
    },
  );
  useSharedSyncStore.getState().clearServerConflicts(serverId);
  useSharedSyncStore.getState().setServerConflict(serverId, false);
}

async function setSharedRemoteChangedFlag(serverId: string, remoteChanged: boolean) {
  const ref = await db.getSharedConfigRefByServerId(serverId);
  if (!ref) return;
  await db.putSharedConfigRef({
    ...ref,
    remoteChanged,
  });
}

export async function resolveSharedQueryConflict(
  queryId: string,
  action: "pushLocal" | "useRemote",
  localSnapshot?: QueryDraftSnapshot,
): Promise<{ appliedSnapshot: QueryDraftSnapshot | null; deleted: boolean }> {
  const conflict = useSharedSyncStore.getState().queryConflicts[queryId];
  if (!conflict) {
    return { appliedSnapshot: null, deleted: false };
  }

  if (action === "pushLocal") {
    const existing = useQueriesStore.getState().queries.find((query) => query.id === queryId);
    if (existing && localSnapshot) {
      await db.updateQuery(queryId, {
        queryText: localSnapshot.queryText,
        variables: localSnapshot.variables,
        headers: localSnapshot.headers,
        remarks: localSnapshot.remarks ?? "",
        riskLevel: localSnapshot.riskLevel ?? DEFAULT_QUERY_RISK_LEVEL,
        requestType: localSnapshot.requestType ?? "graphql",
        ...(localSnapshot.operationConfig !== undefined ? { operationConfig: localSnapshot.operationConfig } : {}),
        restMethod: localSnapshot.restMethod ?? "GET",
        restPath: localSnapshot.restPath ?? "/",
        restParams: localSnapshot.restParams ?? [],
        restContentType: localSnapshot.restContentType ?? "json",
      });
      if (useServersStore.getState().activeServerId === existing.serverId) {
        await refreshQueriesForServer(existing.serverId);
      }
    }
    await createOrUpdateSharedConfig(conflict.serverId);
    useSharedSyncStore.getState().clearQueryConflict(queryId);
    useSharedSyncStore.getState().setServerConflict(conflict.serverId, false);
    await setSharedRemoteChangedFlag(conflict.serverId, false);
    return { appliedSnapshot: localSnapshot ?? null, deleted: false };
  }

  const remoteState = await fetchSharedConfigPayload(conflict.serverId);
  if (!remoteState) {
    return { appliedSnapshot: null, deleted: false };
  }
  const remoteQuery = remoteState.payload.queries.find((query) => query.id === queryId);
  if (remoteQuery) {
    const existing = useQueriesStore.getState().queries.find((query) => query.id === queryId);
    if (existing) {
      const updates: Partial<db.StoredQuery> = {
        name: remoteQuery.name,
        queryText: remoteQuery.queryText,
        variables: remoteQuery.variables,
        headers: remoteQuery.headers,
        remarks: remoteQuery.remarks ?? "",
        riskLevel: remoteQuery.riskLevel ?? DEFAULT_QUERY_RISK_LEVEL,
        requestType: remoteQuery.requestType ?? "graphql",
        ...(remoteQuery.operationConfig !== undefined ? { operationConfig: remoteQuery.operationConfig } : {}),
        restMethod: remoteQuery.restMethod ?? "GET",
        restPath: remoteQuery.restPath ?? "/",
        restParams: remoteQuery.restParams ?? [],
        restContentType: remoteQuery.restContentType ?? "json",
      };
      if (remoteQuery.category !== undefined) updates.category = remoteQuery.category;
      if (remoteQuery.pinnedAt !== undefined) updates.pinnedAt = remoteQuery.pinnedAt;
      await db.updateQuery(queryId, updates);
    }
    markQueriesAsSynced({
      [queryId]: payloadQueryToDraft(remoteQuery),
    });
  } else {
    const wasActiveQuery = useQueriesStore.getState().activeQueryId === queryId;
    await db.deleteQuery(queryId);
    useSharedSyncStore.getState().clearQuerySyncedSnapshots([queryId]);
    useAppStore.getState().closePanesForQuery(queryId);
    if (useServersStore.getState().activeServerId === conflict.serverId) {
      await refreshQueriesForServer(conflict.serverId);
      if (wasActiveQuery) {
        await selectAdHocQuery(
          conflict.serverId,
          defaultAdHocPresetForServer(useServersStore.getState().servers.find((entry) => entry.id === conflict.serverId)),
        );
      }
    }
  }

  if (useServersStore.getState().activeServerId === conflict.serverId) {
    await refreshQueriesForServer(conflict.serverId);
  }
  useSharedSyncStore.getState().clearQueryConflict(queryId);
  const remainingConflicts = Object.values(useSharedSyncStore.getState().queryConflicts)
    .filter((entry) => entry.serverId === conflict.serverId && entry.queryId !== queryId);
  const hasServerConflict = useSharedSyncStore.getState().servers[conflict.serverId]?.hasServerConflict ?? false;
  const remoteChanged = remainingConflicts.length > 0 || hasServerConflict;
  await setSharedRemoteChangedFlag(conflict.serverId, remoteChanged);
  return {
    appliedSnapshot: remoteQuery ? payloadQueryToDraft(remoteQuery) : null,
    deleted: !remoteQuery,
  };
}

export function dismissSharedQueryConflict(queryId: string) {
  useSharedSyncStore.getState().clearQueryConflict(queryId);
}

export async function connectSharedConfig(configId: string, options?: { managedByWorkspace?: boolean }) {
  const displayName = ensureSharedDisplayName();
  const remote = await getSharedConfig(configId);
  const payloadJson = remote.payload;
  const payload = JSON.parse(payloadJson) as SharedConfigPayload;
  const sharedServer = resolveSharedPayloadServer(payload.server);

  const existingRef = await db.getSharedConfigRef(configId);
  const nowIso = new Date().toISOString();
  const existingServer = existingRef
    ? useServersStore.getState().servers.find((entry) => entry.id === existingRef.linkedServerId)
    : undefined;
  const targetServerId = existingServer?.id ?? crypto.randomUUID();
  const server: StoredServer = {
    ...(existingServer ?? {}),
    id: targetServerId,
    ...sharedServer,
    sharedConfigId: configId,
    sharedRole: "editor",
    sharedLastKnownVersion: remote.version,
    sharedCapability: configId,
    sortOrder: existingServer?.sortOrder
      ?? useServersStore.getState().servers.reduce((max, item) => Math.max(max, item.sortOrder), -1) + 1,
    createdAt: existingServer?.createdAt ?? nowIso,
    updatedAt: nowIso,
  };

  const ref: db.StoredSharedConfigRef = {
    configId,
    linkedServerId: targetServerId,
    role: "editor",
    displayName,
    sharedKey: "",
    clientId: "",
    lastKnownVersion: remote.version,
    lastSyncedFingerprint: payloadJson,
    lastSyncedAt: nowIso,
    lastRemoteUpdateAt: remote.updatedAt,
    lastRemoteUpdatedByClientId: remote.updatedBy?.userId ?? "unknown",
    ...(remote.updatedBy?.displayName
      ? { lastRemoteUpdatedByDisplayName: remote.updatedBy.displayName }
      : {}),
    remoteChanged: false,
    ...(options?.managedByWorkspace ? { managedByWorkspace: true } : {}),
    createdAt: nowIso,
    updatedAt: nowIso,
  };
  await db.replaceServerBundle({
    mode: existingServer ? "update" : "create",
    server,
    queries: materializeSharedQueries(targetServerId, payload.queries),
    sharedRef: ref,
  });
  markPayloadQueriesAsSynced(payload.queries);
  await ensureSharedVaultRefs({
    requiredRefs: getSharedPayloadRequiredVaultRefs(payload),
    sharedConfigs: getSharedPayloadSharedConfigs(payload),
    serverId: targetServerId,
  });
  useServersStore.getState().setServers(await db.listServers());
  await selectServer(targetServerId);
  scheduleRuntimeWorkspaceSync();
  return server;
}

// rotateCurrentSharedInvite removed — replaced by email-based invites via inviteToSharedConfig()

export async function deleteCurrentSharedConfig(serverId: string) {
  const ref = await db.getSharedConfigRefByServerId(serverId);
  if (!ref) throw new Error("No shared config ref found");
  await deleteSharedConfig(ref.configId);
  await db.deleteSharedConfigRef(ref.configId);
  await db.setServerSharedState(serverId, {
    sharedConfigId: undefined,
    sharedRole: undefined,
    sharedLastKnownVersion: undefined,
    sharedCapability: undefined,
  });
  useServersStore.getState().setServers(await db.listServers());
  scheduleRuntimeWorkspaceSync();
}

function materializeSharedQueries(
  serverId: string,
  queries: SharedConfigPayload["queries"],
): StoredQuery[] {
  const nowIso = new Date().toISOString();
  const existingById = new Map(
    useQueriesStore.getState().queries
      .filter((query) => query.serverId === serverId)
      .map((query) => [query.id, query]),
  );
  return queries.map((query, index) => ({
    id: query.id,
    serverId,
    name: query.name,
    queryText: query.queryText,
    variables: query.variables,
    headers: query.headers,
    remarks: query.remarks ?? "",
    riskLevel: query.riskLevel ?? DEFAULT_QUERY_RISK_LEVEL,
    sortOrder: index + 10,
    createdAt: existingById.get(query.id)?.createdAt ?? nowIso,
    updatedAt: nowIso,
    ...(query.category ? { category: query.category } : {}),
    ...(query.pinnedAt !== undefined ? { pinnedAt: query.pinnedAt } : {}),
    ...(query.requestType ? { requestType: query.requestType } : {}),
    ...(query.operationConfig ? { operationConfig: query.operationConfig } : {}),
    ...(query.restMethod ? { restMethod: query.restMethod } : {}),
    ...(query.restPath != null ? { restPath: query.restPath } : {}),
    ...(query.restParams ? { restParams: query.restParams } : {}),
    ...(query.restContentType ? { restContentType: query.restContentType } : {}),
    ...(existingById.get(query.id)?.lastUsedAt !== undefined ? { lastUsedAt: existingById.get(query.id)?.lastUsedAt ?? null } : {}),
  }));
}

async function collectSharedConfigValues(
  serverId: string,
  server: StoredServer,
  queries: StoredQuery[],
): Promise<SharedConfigValue[]> {
  const refs = new Map<string, SharedConfigRequiredVaultRef>();
  const addRefs = (items: SharedConfigRequiredVaultRef[]) => {
    for (const item of items) {
      if (item.kind === "config") {
        refs.set(`${item.kind}:${item.key}`, item);
      }
    }
  };

  addRefs(collectStringVaultRefs(server.baseUrl));
  if (server.graphqlPath) addRefs(collectStringVaultRefs(server.graphqlPath));
  if (server.restPath) addRefs(collectStringVaultRefs(server.restPath));
  addRefs(collectJsonVaultRefs(server.connectionConfig));
  addRefs(collectStringVaultRefs(server.authConfig));

  for (const query of queries) {
    addRefs(collectStringVaultRefs(query.headers));
    addRefs(collectStringVaultRefs(query.variables));
    if (query.restPath) addRefs(collectStringVaultRefs(query.restPath));
    addRefs(collectJsonVaultRefs(query.operationConfig));
  }

  if (refs.size === 0) {
    return [];
  }

  const entries = await db.listVaultEntries();
  const sharedConfigs = new Map<string, SharedConfigValue>();
  const missingRefs: PendingVaultRefDraft[] = [];

  for (const ref of refs.values()) {
    const entry = entries.find((candidate) =>
      candidate.kind === "config"
      && candidate.key === ref.key
      && candidate.scopeType === "server"
      && candidate.scopeServerId === serverId,
    ) ?? entries.find((candidate) =>
      candidate.kind === "config"
      && candidate.key === ref.key
      && candidate.scopeType === "global",
    );

    if (!entry) {
      missingRefs.push({ kind: "config", key: ref.key, scopeType: "global" });
      continue;
    }

    const value = await readVaultEntryValue(entry.id);
    sharedConfigs.set(`${entry.scopeType}:${entry.key}`, {
      key: entry.key,
      value,
      scopeType: entry.scopeType,
      ...(entry.description ? { description: entry.description } : {}),
    });
  }

  if (missingRefs.length > 0) {
    useAppStore.getState().setPendingVaultRefs(missingRefs);
    useAppStore.getState().setShowVaultManager(true);
    throw new MissingVaultConfigError(missingRefs.map((r) => r.key));
  }

  return [...sharedConfigs.values()].sort((a, b) =>
    a.scopeType === b.scopeType
      ? a.key.localeCompare(b.key, undefined, { sensitivity: "base" })
      : a.scopeType.localeCompare(b.scopeType),
  );
}

function collectReferencedConfigKeys(server: StoredServer, queries: StoredQuery[]): Set<string> {
  const keys = new Set<string>();
  const addRefs = (items: SharedConfigRequiredVaultRef[]) => {
    for (const item of items) {
      if (item.kind === "config") {
        keys.add(item.key);
      }
    }
  };

  addRefs(collectStringVaultRefs(server.baseUrl));
  if (server.graphqlPath) addRefs(collectStringVaultRefs(server.graphqlPath));
  if (server.restPath) addRefs(collectStringVaultRefs(server.restPath));
  addRefs(collectJsonVaultRefs(server.connectionConfig));
  addRefs(collectStringVaultRefs(server.authConfig));

  for (const query of queries) {
    addRefs(collectStringVaultRefs(query.headers));
    addRefs(collectStringVaultRefs(query.variables));
    if (query.restPath) addRefs(collectStringVaultRefs(query.restPath));
    addRefs(collectJsonVaultRefs(query.operationConfig));
  }

  return keys;
}

async function scheduleSharedPushesForVaultConfig(
  input: {
    kind: db.VaultEntryKind;
    key: string;
    scopeType: db.VaultScopeType;
    scopeServerId?: string;
  },
  vaultEntries: StoredVaultEntry[],
) {
  if (input.kind !== "config") {
    return;
  }

  const configKey = input.key.trim();
  if (!configKey) {
    return;
  }

  const sharedServers = useServersStore.getState().servers
    .filter((server) => server.sharedConfigId && server.sharedCapability);
  if (sharedServers.length === 0) {
    return;
  }

  for (const server of sharedServers) {
    if (input.scopeType === "server" && input.scopeServerId !== server.id) {
      continue;
    }

    const queries = (await db.listQueries(server.id)).filter((query) => !isAdHocQuery(query));
    const referencedConfigKeys = collectReferencedConfigKeys(server, queries);
    if (!referencedConfigKeys.has(configKey)) {
      continue;
    }

    if (input.scopeType === "global") {
      const hasServerOverride = vaultEntries.some((entry) =>
        entry.kind === "config"
        && entry.key === configKey
        && entry.scopeType === "server"
        && entry.scopeServerId === server.id,
      );
      if (hasServerOverride) {
        continue;
      }
    }

    await scheduleSharedPushIfConnected(server.id);
  }
}

export async function getSharedRefForServer(serverId: string) {
  return db.getSharedConfigRefByServerId(serverId);
}

// ── Export/Import ────────────────────────────────────────────────────

export async function exportData() {
  return db.exportAllData();
}

export async function exportServerData(serverId: string) {
  return db.exportServerData(serverId);
}

export async function importData(data: SpuerhundExport | LegacySpuerhundExport) {
  await db.importData(data);
  await initialize(); // reload everything
}

export async function exportVault() {
  return exportVaultSnapshot();
}

export async function importVault(data: SpuerhundVaultExport) {
  const snapshot = await importVaultSnapshot(data);
  useVaultStore.getState().setSnapshot({
    hasVault: snapshot.hasVault,
    isUnlocked: snapshot.isUnlocked,
    isRemembered: snapshot.isRemembered,
    entries: snapshot.entries,
  });
}

export async function refreshVaultInApp() {
  const snapshot = await refreshVaultSnapshot();
  useVaultStore.getState().setSnapshot({
    hasVault: snapshot.hasVault,
    isUnlocked: snapshot.isUnlocked,
    isRemembered: snapshot.isRemembered,
    entries: snapshot.entries,
  });
  return snapshot;
}

export { handleRuntimeSetupError };

export async function createVaultInApp(passphrase: string, rememberOnDevice = false) {
  const snapshot = await createVault(passphrase, { rememberOnDevice });
  useVaultStore.getState().setSnapshot({
    hasVault: snapshot.hasVault,
    isUnlocked: snapshot.isUnlocked,
    isRemembered: snapshot.isRemembered,
    entries: snapshot.entries,
  });
  await materializePendingVaultRefsInApp();
}

export async function unlockVaultInApp(passphrase: string, rememberOnDevice = false) {
  const snapshot = await unlockVault(passphrase, { rememberOnDevice });
  useVaultStore.getState().setSnapshot({
    hasVault: snapshot.hasVault,
    isUnlocked: snapshot.isUnlocked,
    isRemembered: snapshot.isRemembered,
    entries: snapshot.entries,
  });
  await materializePendingVaultRefsInApp();
}

export function lockVaultInApp() {
  lockBrowserVault();
  const state = useVaultStore.getState();
  useVaultStore.getState().setSnapshot({
    hasVault: state.hasVault,
    isUnlocked: false,
    isRemembered: state.isRemembered,
    entries: state.entries,
  });
}

export async function forgetRememberedVaultInApp() {
  const snapshot = await forgetRememberedVault();
  useVaultStore.getState().setSnapshot({
    hasVault: snapshot.hasVault,
    isUnlocked: snapshot.isUnlocked,
    isRemembered: snapshot.isRemembered,
    entries: snapshot.entries,
  });
}

export async function readVaultValue(id: string) {
  return readVaultEntryValue(id);
}

export async function saveVaultEntryInApp(input: {
  id?: string;
  key: string;
  kind: db.VaultEntryKind;
  scopeType: db.VaultScopeType;
  scopeServerId?: string;
  description?: string;
  value: string;
}) {
  const snapshot = await saveVaultEntry(input);
  useVaultStore.getState().setSnapshot({
    hasVault: snapshot.hasVault,
    isUnlocked: snapshot.isUnlocked,
    isRemembered: snapshot.isRemembered,
    entries: snapshot.entries,
  });
  // Sync saved entry to D1
  const saved = snapshot.entries.find((e) =>
    e.key === input.key.trim() && e.kind === input.kind
    && e.scopeType === input.scopeType
    && (input.scopeType === "server" ? e.scopeServerId === input.scopeServerId : true),
  );
  if (saved) {
    syncVaultEntryToD1({
      id: saved.id,
      ...(saved.scopeServerId ? { serverId: saved.scopeServerId } : {}),
      kind: saved.kind,
      key: saved.key,
      ciphertext: saved.ciphertext,
      iv: saved.iv,
      createdAt: saved.createdAt,
      updatedAt: saved.updatedAt,
    });
  }
  await scheduleSharedPushesForVaultConfig(input, snapshot.entries);
}

export async function deleteVaultEntryInApp(id: string) {
  const snapshot = await removeVaultEntry(id);
  useVaultStore.getState().setSnapshot({
    hasVault: snapshot.hasVault,
    isUnlocked: snapshot.isUnlocked,
    isRemembered: snapshot.isRemembered,
    entries: snapshot.entries,
  });
  syncDeleteVaultEntryFromD1(id);
}

// Re-export types for convenience
export type {
  SpuerhundExport,
  SpuerhundVaultExport,
  RequestType,
  RestMethod,
  RestContentType,
} from "./persistence";
